import { useState, useMemo, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { QuotePortalModal } from './Billing';
import { Campaigns, CampaignTemplatesTab } from './Campaigns';

// ─── Shared Styles ─────────────────────────────────────────────────

const pageCardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--spacing-4)',
  boxShadow: 'var(--shadow-sm)',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: 'var(--spacing-2) var(--spacing-3)',
  backgroundColor: 'var(--color-background)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
  fontSize: 'var(--font-size-sm)',
};

const textareaStyle: React.CSSProperties = {
  ...inputStyle,
  minHeight: 120,
  resize: 'vertical' as const,
  fontFamily: 'inherit',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.05em',
  color: 'var(--color-text-muted)',
  marginBottom: 'var(--spacing-1)',
};

const primaryButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  backgroundColor: 'var(--color-accent)',
  border: 'none',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text-inverse)',
  fontWeight: 600,
  cursor: 'pointer',
};

const secondaryButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-4)',
  backgroundColor: 'var(--color-surface-elevated)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
  fontWeight: 500,
  cursor: 'pointer',
};

const smallButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-1) var(--spacing-2)',
  fontSize: 'var(--font-size-xs)',
  borderRadius: 'var(--radius-default)',
  border: '1px solid var(--color-border)',
  backgroundColor: 'var(--color-surface-elevated)',
  color: 'var(--color-text)',
  cursor: 'pointer',
};

const tabStyle = (active: boolean): React.CSSProperties => ({
  padding: 'var(--spacing-2) var(--spacing-4)',
  border: 'none',
  borderBottom: active ? '2px solid var(--color-accent)' : '2px solid transparent',
  backgroundColor: 'transparent',
  color: active ? 'var(--color-accent)' : 'var(--color-text-muted)',
  fontWeight: active ? 600 : 400,
  cursor: 'pointer',
  fontSize: 'var(--font-size-sm)',
});

const badgeStyle = (color: string): React.CSSProperties => ({
  display: 'inline-block',
  padding: '2px 8px',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  borderRadius: 'var(--radius-full)',
  backgroundColor:
    color === 'gold'
      ? 'rgba(217, 178, 86, 0.15)'
      : color === 'green'
        ? 'rgba(40, 167, 69, 0.15)'
        : color === 'red'
          ? 'rgba(220, 53, 69, 0.15)'
          : 'rgba(108, 117, 125, 0.15)',
  color:
    color === 'gold'
      ? 'var(--color-accent)'
      : color === 'green'
        ? '#28a745'
        : color === 'red'
          ? '#dc3545'
          : 'var(--color-text-muted)',
});

// ─── Main Component ────────────────────────────────────────────────

type Tab = 'inbox' | 'templates' | 'contacts' | 'send' | 'log' | 'scheduled' | 'contractor-unwell' | 'campaigns';

export function Messaging() {
  const [searchParams] = useSearchParams();
  // Deep links (e.g. /messaging?tab=campaigns, or /campaigns?open=<id> redirected
  // here) land on the right tab.
  const initialTab = ((): Tab => {
    const t = searchParams.get('tab');
    const allowed: Tab[] = ['inbox', 'templates', 'contacts', 'send', 'log', 'scheduled', 'contractor-unwell', 'campaigns'];
    return (allowed as string[]).includes(t || '') ? (t as Tab) : 'scheduled';
  })();
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);

  // When the active tab changes, scroll it into view within the scrollable
  // tab bar — matters on mobile when later tabs (Scheduled, Contractor Unwell)
  // are off-screen. See .claude/skills/ui-ux-admin/SKILL.md §3.
  useEffect(() => {
    const el = document.querySelector('.tab-active');
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [activeTab]);

  return (
    <div style={{ padding: 'var(--spacing-4)', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 'var(--spacing-4)' }}>
        <h1 style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, margin: 0 }}>Messaging</h1>
        <p
          style={{
            color: 'var(--color-text-muted)',
            margin: '4px 0 0',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          Manage templates, customers, and send SMS & Email campaigns
        </p>
      </div>

      {/* Horizontal-scroll tab bar with fade gradient on overflow edges.
          See .claude/skills/ui-ux-admin/SKILL.md §3 */}
      <div
        className="tab-bar-wrapper"
        style={{
          borderBottom: '1px solid var(--color-border)',
          marginBottom: 'var(--spacing-4)',
        }}
      >
        <div
          className="tab-bar"
          style={{
            display: 'flex',
            gap: 'var(--spacing-1)',
            overflowX: 'auto',
            flexWrap: 'nowrap',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
          }}
        >
          <button
            style={{ ...tabStyle(activeTab === 'scheduled'), flexShrink: 0 }}
            className={activeTab === 'scheduled' ? 'tab-active' : undefined}
            onClick={() => setActiveTab('scheduled')}
          >
            🕐 Scheduled
          </button>
          <button
            style={{ ...tabStyle(activeTab === 'log'), flexShrink: 0 }}
            className={activeTab === 'log' ? 'tab-active' : undefined}
            onClick={() => setActiveTab('log')}
          >
            📋 Send Log
          </button>
          <button
            style={{ ...tabStyle(activeTab === 'campaigns'), flexShrink: 0 }}
            className={activeTab === 'campaigns' ? 'tab-active' : undefined}
            onClick={() => setActiveTab('campaigns')}
          >
            📣 Campaigns
          </button>
          <button
            style={{ ...tabStyle(activeTab === 'templates'), flexShrink: 0 }}
            className={activeTab === 'templates' ? 'tab-active' : undefined}
            onClick={() => setActiveTab('templates')}
          >
            📝 Templates
          </button>
          <button
            style={{ ...tabStyle(activeTab === 'send'), flexShrink: 0 }}
            className={activeTab === 'send' ? 'tab-active' : undefined}
            onClick={() => setActiveTab('send')}
          >
            📤 Send Message
          </button>
        </div>
      </div>

      {activeTab === 'templates' && <TemplatesTab />}
      {activeTab === 'send' && <SendTab />}
      {activeTab === 'log' && <SendLogTab />}
      {activeTab === 'scheduled' && <ScheduledTab />}
      {activeTab === 'campaigns' && <Campaigns embedded />}
    </div>
  );
}

// ─── Inbox Tab ─────────────────────────────────────────────────────

function InboxTab() {
  const qc = useQueryClient();
  const [page, setPage] = useState(0);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const limit = 25;

  const { data, isLoading } = useQuery({
    queryKey: ['messaging-inbound', page, unreadOnly],
    queryFn: () =>
      api.getInboundMessages({
        limit,
        offset: page * limit,
        unreadOnly: unreadOnly || undefined,
      }),
    refetchInterval: 30_000,
  });

  const markReadMutation = useMutation({
    mutationFn: (ids: number[]) => api.markInboundRead(ids),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['messaging-inbound'] }),
  });

  const messages = data?.messages || [];
  const total = data?.total || 0;
  const unreadCount = data?.unreadCount || 0;
  const totalPages = Math.ceil(total / limit);

  const handleMarkAllRead = () => {
    const unreadIds = messages.filter((m: any) => !m.read).map((m: any) => m.id);
    if (unreadIds.length > 0) markReadMutation.mutate(unreadIds);
  };

  const handleMarkRead = (id: number) => {
    markReadMutation.mutate([id]);
  };

  return (
    <div>
      <div
        style={{
          display: 'flex',
          gap: 'var(--spacing-2)',
          marginBottom: 'var(--spacing-3)',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <label
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-muted)',
            cursor: 'pointer',
          }}
        >
          <input
            type="checkbox"
            checked={unreadOnly}
            onChange={(e) => {
              setUnreadOnly(e.target.checked);
              setPage(0);
            }}
          />
          Unread only
        </label>
        {unreadCount > 0 && (
          <button
            style={smallButtonStyle}
            onClick={handleMarkAllRead}
            disabled={markReadMutation.isPending}
          >
            Mark page as read
          </button>
        )}
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-muted)',
            marginLeft: 'auto',
          }}
        >
          {total} messages{unreadCount > 0 && ` · ${unreadCount} unread`}
        </span>
      </div>

      {isLoading ? (
        <div style={{ color: 'var(--color-text-muted)' }}>Loading inbox...</div>
      ) : (
        <>
          {/* Mobile: stacked card list. See .claude/skills/ui-ux-admin/SKILL.md §2 */}
          <div className="resp-cards">
            {messages.map((m: any) => {
              const isExpanded = expandedId === m.id;
              return (
                <div
                  key={m.id}
                  onClick={() => setExpandedId(isExpanded ? null : m.id)}
                  style={{
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-default)',
                    padding: 'var(--spacing-3)',
                    marginBottom: 'var(--spacing-2)',
                    backgroundColor: !m.read ? 'rgba(217, 178, 86, 0.04)' : 'var(--color-surface)',
                    fontWeight: !m.read ? 500 : 400,
                    cursor: 'pointer',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: 'var(--spacing-1)',
                      gap: 'var(--spacing-2)',
                    }}
                  >
                    <span style={{ fontWeight: 600, fontSize: 'var(--font-size-md)' }}>
                      {m.customer_name || m.from_number}
                    </span>
                    <span style={badgeStyle(m.read ? 'grey' : 'gold')}>
                      {m.read ? 'Read' : 'New'}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-muted)',
                      marginBottom: 'var(--spacing-2)',
                    }}
                  >
                    {m.from_number} ·{' '}
                    {new Date(m.received_at).toLocaleString('en-AU', {
                      timeZone: 'Australia/Brisbane',
                    })}
                  </div>
                  <div
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      whiteSpace: isExpanded ? 'pre-wrap' : 'nowrap',
                      overflow: isExpanded ? 'visible' : 'hidden',
                      textOverflow: isExpanded ? 'unset' : 'ellipsis',
                      lineHeight: 1.5,
                    }}
                  >
                    {m.body}
                    {m.num_media > 0 && (
                      <span style={{ color: 'var(--color-text-muted)', marginLeft: 6 }}>
                        📎 {m.num_media}
                      </span>
                    )}
                  </div>
                  {!m.read && (
                    <button
                      style={{ ...smallButtonStyle, marginTop: 'var(--spacing-2)', minHeight: 44 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkRead(m.id);
                      }}
                    >
                      Mark read
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Desktop: HTML table (preserved as-is for expand-row interactions) */}
          <div className="resp-table" style={{ overflowX: 'auto' }}>
            <table
              style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
            >
              <thead>
                <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>From</th>
                  <th style={thStyle}>Customer</th>
                  <th style={thStyle}>Message</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {messages.flatMap((m: any) => [
                  <tr
                    key={m.id}
                    style={{
                      borderBottom: expandedId === m.id ? 'none' : '1px solid var(--color-border)',
                      cursor: 'pointer',
                      backgroundColor:
                        expandedId === m.id
                          ? 'rgba(217, 178, 86, 0.06)'
                          : !m.read
                            ? 'rgba(217, 178, 86, 0.03)'
                            : undefined,
                      fontWeight: !m.read ? 600 : 400,
                    }}
                    onClick={() => setExpandedId(expandedId === m.id ? null : m.id)}
                    title="Click to view full message"
                  >
                    <td style={tdStyle}>
                      {new Date(m.received_at).toLocaleString('en-AU', {
                        timeZone: 'Australia/Brisbane',
                      })}
                    </td>
                    <td style={tdStyle}>{m.from_number}</td>
                    <td style={tdStyle}>{m.customer_name || '—'}</td>
                    <td
                      style={{
                        ...tdStyle,
                        maxWidth: 300,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {m.body}
                    </td>
                    <td style={tdStyle}>
                      <span style={badgeStyle(m.read ? 'grey' : 'gold')}>
                        {m.read ? 'Read' : 'New'}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      {!m.read && (
                        <button
                          style={smallButtonStyle}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkRead(m.id);
                          }}
                        >
                          Mark read
                        </button>
                      )}
                    </td>
                  </tr>,
                  ...(expandedId === m.id
                    ? [
                        <tr
                          key={`${m.id}-body`}
                          style={{ borderBottom: '1px solid var(--color-border)' }}
                        >
                          <td colSpan={6} style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}>
                            <div
                              style={{
                                background: 'var(--color-background-alt, rgba(0,0,0,0.15))',
                                borderRadius: 'var(--border-radius, 6px)',
                                padding: 'var(--spacing-3)',
                                fontSize: 'var(--font-size-sm)',
                                whiteSpace: 'pre-wrap',
                                lineHeight: 1.5,
                                color: 'var(--color-text)',
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: 600,
                                  marginBottom: 'var(--spacing-1)',
                                  color: 'var(--color-accent, #D9B256)',
                                }}
                              >
                                Full Message
                              </div>
                              {m.body || '(empty)'}
                              {m.num_media > 0 && (
                                <div
                                  style={{
                                    marginTop: 'var(--spacing-2)',
                                    color: 'var(--color-text-muted)',
                                  }}
                                >
                                  📎 {m.num_media} media attachment{m.num_media > 1 ? 's' : ''}
                                </div>
                              )}
                              <div
                                style={{
                                  marginTop: 'var(--spacing-2)',
                                  fontSize: 'var(--font-size-xs)',
                                  color: 'var(--color-text-muted)',
                                }}
                              >
                                SID: {m.twilio_message_sid}
                              </div>
                            </div>
                          </td>
                        </tr>,
                      ]
                    : []),
                ])}
              </tbody>
            </table>
          </div>

          {messages.length === 0 && (
            <div
              style={{
                color: 'var(--color-text-muted)',
                textAlign: 'center',
                padding: 'var(--spacing-4)',
              }}
            >
              No inbound messages yet.
            </div>
          )}

          {totalPages > 1 && (
            <div
              style={{
                display: 'flex',
                gap: 'var(--spacing-2)',
                alignItems: 'center',
                marginTop: 'var(--spacing-3)',
                justifyContent: 'center',
              }}
            >
              <button
                style={smallButtonStyle}
                disabled={page === 0}
                onClick={() => setPage(page - 1)}
              >
                ‹ Prev
              </button>
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                Page {page + 1} of {totalPages}
              </span>
              <button
                style={smallButtonStyle}
                disabled={page >= totalPages - 1}
                onClick={() => setPage(page + 1)}
              >
                Next ›
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Templates Tab ─────────────────────────────────────────────────

// Unified Templates home: operational (transactional) + campaign (bulk marketing)
// templates in one place, clearly differentiated. Two stores under the hood.
function TemplatesTab() {
  const sectionHead = (bg: string, border: string): React.CSSProperties => ({
    background: bg, border: `1px solid ${border}`, borderLeft: `4px solid ${border}`,
    borderRadius: 8, padding: 'var(--spacing-2) var(--spacing-3)', margin: '0 0 var(--spacing-2)',
  });
  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacing-3)', marginBottom: 'var(--spacing-3)' }}>
        {/* OPERATIONAL section */}
        <div style={{ flex: '1 1 100%' }}>
          <div style={sectionHead('rgba(0,176,151,0.06)', 'var(--color-accent, #00B097)')}>
            <div style={{ fontWeight: 700, fontSize: 'var(--font-size-md)' }}>⚙️ Operational templates</div>
            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
              Automatic transactional messages to a single customer — arrival reminders, quote follow-ups, invoice SMS, review/referral asks. Tokens look like <code>{'{name}'}</code>.
            </div>
          </div>
          <OperationalTemplatesTab />
        </div>
        {/* CAMPAIGN section */}
        <div style={{ flex: '1 1 100%' }}>
          <div style={sectionHead('rgba(254,197,83,0.12)', 'var(--color-gold, #FEC553)')}>
            <div style={{ fontWeight: 700, fontSize: 'var(--font-size-md)' }}>📣 Campaign templates</div>
            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
              Bulk marketing sends to an audience — dormant win-back, referral reactivation. Tokens look like <code>{'{{first_name}}'}</code>.
            </div>
          </div>
          <CampaignTemplatesTab />
        </div>
      </div>
    </div>
  );
}

function OperationalTemplatesTab() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<any | null>(null);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  const [channelFilter, setChannelFilter] = useState<string>('');
  const [pendingDeleteTemplate, setPendingDeleteTemplate] = useState<any | null>(null);

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['messaging-templates', channelFilter],
    queryFn: () => api.getTemplates(channelFilter || undefined),
  });

  const createMutation = useMutation({
    mutationFn: (data: any) => api.createTemplate(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['messaging-templates'] });
      setCreating(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => api.updateTemplate(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['messaging-templates'] });
      setEditing(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.deleteTemplate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['messaging-templates'] }),
  });

  const dupMutation = useMutation({
    mutationFn: (id: number) => api.duplicateTemplate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['messaging-templates'] }),
  });

  const filtered = useMemo(() => {
    if (!search) return templates;
    const s = search.toLowerCase();
    return templates.filter(
      (t: any) => t.name.toLowerCase().includes(s) || t.body.toLowerCase().includes(s),
    );
  }, [templates, search]);

  if (isLoading)
    return <div style={{ color: 'var(--color-text-muted)' }}>Loading templates...</div>;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          gap: 'var(--spacing-2)',
          marginBottom: 'var(--spacing-3)',
          flexWrap: 'wrap',
        }}
      >
        <input
          style={{ ...inputStyle, maxWidth: 300 }}
          placeholder="Search templates..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          style={{ ...inputStyle, maxWidth: 150 }}
          value={channelFilter}
          onChange={(e) => setChannelFilter(e.target.value)}
        >
          <option value="">All channels</option>
          <option value="sms">SMS</option>
          <option value="email">Email</option>
        </select>
        <button
          style={primaryButtonStyle}
          onClick={() => {
            setCreating(true);
            setEditing(null);
          }}
        >
          + New Template
        </button>
      </div>

      {(creating || editing) && (
        <div style={{ ...pageCardStyle, marginBottom: 'var(--spacing-3)' }}>
          <TemplateForm
            template={editing}
            onSave={(data) => {
              if (editing) {
                updateMutation.mutate({ id: editing.id, data });
              } else {
                createMutation.mutate(data);
              }
            }}
            onCancel={() => {
              setEditing(null);
              setCreating(false);
            }}
            saving={createMutation.isPending || updateMutation.isPending}
          />
        </div>
      )}

      <div style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
        {filtered.map((t: any) => (
          <div
            key={t.id}
            className="template-card"
            style={{
              ...pageCardStyle,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 'var(--spacing-3)',
              flexWrap: 'wrap',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  display: 'flex',
                  gap: 'var(--spacing-2)',
                  alignItems: 'center',
                  marginBottom: 4,
                }}
              >
                <strong>{t.name}</strong>
                <span style={badgeStyle(t.channel === 'sms' ? 'gold' : 'green')}>
                  {t.channel.toUpperCase()}
                </span>
                {t.category && <span style={badgeStyle('grey')}>{t.category}</span>}
                {t.is_default && (
                  <span
                    style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}
                  >
                    Default
                  </span>
                )}
              </div>
              {t.channel === 'email' && t.subject && (
                <div
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-muted)',
                    marginBottom: 4,
                  }}
                >
                  Subject: {t.subject}
                </div>
              )}
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-text-muted)',
                  whiteSpace: 'pre-wrap',
                  maxHeight: 60,
                  overflow: 'hidden',
                }}
              >
                {t.body}
              </div>
              {t.variables?.length > 0 && (
                <div style={{ marginTop: 4, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {t.variables.map((v: string) => (
                    <span
                      key={v}
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        padding: '1px 6px',
                        backgroundColor: 'rgba(217, 178, 86, 0.1)',
                        borderRadius: 4,
                        color: 'var(--color-accent)',
                      }}
                    >
                      {`{${v}}`}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div
              className="template-card-actions"
              style={{ display: 'flex', gap: 'var(--spacing-1)', flexShrink: 0 }}
            >
              <button
                aria-label="Edit template"
                title="Edit"
                style={{ ...smallButtonStyle, minHeight: 44 }}
                onClick={() => {
                  setEditing(t);
                  setCreating(false);
                }}
              >
                <span className="btn-label">Edit</span>
                <span className="btn-icon" aria-hidden="true">
                  ✏️
                </span>
              </button>
              <button
                aria-label="Duplicate template"
                title="Duplicate"
                style={{ ...smallButtonStyle, minHeight: 44 }}
                onClick={() => dupMutation.mutate(t.id)}
              >
                <span className="btn-label">Dup</span>
                <span className="btn-icon" aria-hidden="true">
                  📄
                </span>
              </button>
              <button
                aria-label="Delete template"
                title="Delete"
                style={{
                  ...smallButtonStyle,
                  minHeight: 44,
                  color: 'var(--color-error)',
                  borderColor: 'rgba(220,53,69,0.25)',
                }}
                onClick={() => setPendingDeleteTemplate(t)}
              >
                <span className="btn-label">Del</span>
                <span className="btn-icon" aria-hidden="true">
                  🗑️
                </span>
              </button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div
            style={{
              color: 'var(--color-text-muted)',
              textAlign: 'center',
              padding: 'var(--spacing-4)',
            }}
          >
            No templates found. Create one to get started.
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={!!pendingDeleteTemplate}
        title={`Delete "${pendingDeleteTemplate?.name ?? 'template'}"?`}
        message="This template will be permanently removed."
        confirmLabel="Delete"
        onConfirm={() => {
          if (pendingDeleteTemplate) deleteMutation.mutate(pendingDeleteTemplate.id);
          setPendingDeleteTemplate(null);
        }}
        onCancel={() => setPendingDeleteTemplate(null)}
      />
    </div>
  );
}

// ─── Template Form ─────────────────────────────────────────────────

function TemplateForm({
  template,
  onSave,
  onCancel,
  saving,
}: {
  template?: any;
  onSave: (data: any) => void;
  onCancel: () => void;
  saving: boolean;
}) {
  const [name, setName] = useState(template?.name || '');
  const [channel, setChannel] = useState<string>(template?.channel || 'sms');
  const [subject, setSubject] = useState(template?.subject || '');
  const [body, setBody] = useState(template?.body || '');
  const [category, setCategory] = useState(template?.category || '');
  const [variablesStr, setVariablesStr] = useState((template?.variables || []).join(', '));

  const canSave = name.trim() && body.trim() && !saving;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      name: name.trim(),
      channel,
      subject: channel === 'email' ? subject.trim() : null,
      body: body.trim(),
      variables: variablesStr
        .split(',')
        .map((v: string) => v.trim())
        .filter(Boolean),
      category: category || null,
    });
  };

  const insertVariable = (varName: string) => {
    setBody((prev: string) => prev + `{${varName}}`);
  };

  const commonVars = [
    'first_name',
    'last_name',
    'full_name',
    'booking_url',
    'rebook_url',
    'appointment_date',
    'appointment_time',
    'location_name',
    'contractor_name',
    'promo_details',
    'promo_title',
  ];

  return (
    <form onSubmit={handleSubmit}>
      <h3 style={{ margin: '0 0 var(--spacing-3)', fontWeight: 600 }}>
        {template ? 'Edit Template' : 'New Template'}
      </h3>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 'var(--spacing-3)',
          marginBottom: 'var(--spacing-3)',
        }}
      >
        <div>
          <label style={labelStyle}>Name</label>
          <input
            style={inputStyle}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Template name"
          />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-2)' }}>
          <div>
            <label style={labelStyle}>Channel</label>
            <select style={inputStyle} value={channel} onChange={(e) => setChannel(e.target.value)}>
              <option value="sms">SMS</option>
              <option value="email">Email</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Category</label>
            <select
              style={inputStyle}
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              <option value="">None</option>
              <option value="reminder">Reminder</option>
              <option value="rebooking">Rebooking</option>
              <option value="promotion">Promotion</option>
              <option value="follow_up">Follow-up</option>
              <option value="custom">Custom</option>
            </select>
          </div>
        </div>
      </div>

      {channel === 'email' && (
        <div style={{ marginBottom: 'var(--spacing-3)' }}>
          <label style={labelStyle}>Subject</label>
          <input
            style={inputStyle}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Email subject line"
          />
        </div>
      )}

      <div style={{ marginBottom: 'var(--spacing-2)' }}>
        <label style={labelStyle}>Message Body</label>
        <div style={{ marginBottom: 4, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-text-muted)',
              lineHeight: '24px',
            }}
          >
            Insert:
          </span>
          {commonVars.map((v) => (
            <button
              key={v}
              type="button"
              style={{ ...smallButtonStyle, fontSize: '11px', padding: '2px 6px' }}
              onClick={() => insertVariable(v)}
            >
              {`{${v}}`}
            </button>
          ))}
        </div>
        <textarea
          style={textareaStyle}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={
            channel === 'sms'
              ? 'SMS message body (160 chars recommended)...'
              : 'Email body content...'
          }
        />
        {channel === 'sms' && (
          <div
            style={{
              fontSize: 'var(--font-size-xs)',
              color: body.length > 160 ? 'var(--color-error)' : 'var(--color-text-muted)',
              marginTop: 2,
            }}
          >
            {body.length} / 160 characters{' '}
            {body.length > 160 && '(will be split into multiple segments)'}
          </div>
        )}
      </div>

      <div style={{ marginBottom: 'var(--spacing-3)' }}>
        <label style={labelStyle}>Variables (comma separated)</label>
        <input
          style={inputStyle}
          value={variablesStr}
          onChange={(e) => setVariablesStr(e.target.value)}
          placeholder="first_name, appointment_date, booking_url"
        />
      </div>

      <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
        <button
          type="submit"
          style={{ ...primaryButtonStyle, opacity: canSave ? 1 : 0.5 }}
          disabled={!canSave}
        >
          {saving ? 'Saving...' : template ? 'Update Template' : 'Create Template'}
        </button>
        <button type="button" style={secondaryButtonStyle} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

// ─── Contacts Tab ──────────────────────────────────────────────────

// ─── Customer form (add / edit) ────────────────────────────────────

const MARKETING_STATUS_OPTIONS = [
  { value: 'subscribed', label: 'Subscribed' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
  { value: 'pending', label: 'Pending' },
];

interface CustomerFormData {
  full_name: string;
  email: string;
  mobile: string;
  phone: string;
  sms_marketing_status: string;
  email_marketing_status: string;
}

const emptyForm: CustomerFormData = {
  full_name: '',
  email: '',
  mobile: '',
  phone: '',
  sms_marketing_status: 'subscribed',
  email_marketing_status: 'subscribed',
};

function CustomerModal({
  customer,
  onClose,
}: {
  customer: any | null; // null = add mode
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState<CustomerFormData>(
    customer
      ? {
          full_name: customer.full_name || '',
          email: customer.email || '',
          mobile: customer.mobile || '',
          phone: customer.phone || '',
          sms_marketing_status: customer.sms_marketing_status || 'subscribed',
          email_marketing_status: customer.email_marketing_status || 'subscribed',
        }
      : emptyForm,
  );
  const [error, setError] = useState('');

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        full_name: form.full_name.trim(),
        email: form.email.trim() || null,
        mobile: form.mobile.trim() || null,
        phone: form.phone.trim() || null,
        sms_marketing_status: form.sms_marketing_status,
        email_marketing_status: form.email_marketing_status,
      };
      if (customer) {
        return api.updateContact(customer.id, payload);
      }
      return api.createContact(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['messaging-contacts'] });
      onClose();
    },
    onError: (err: any) => {
      setError(err?.message || 'Failed to save customer');
    },
  });

  const field = (label: string, key: keyof CustomerFormData, type = 'text') => (
    <div style={{ marginBottom: 'var(--spacing-3)' }}>
      <label style={labelStyle}>{label}</label>
      <input
        style={inputStyle}
        type={type}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
      />
    </div>
  );

  const select = (
    label: string,
    key: keyof CustomerFormData,
    options: typeof MARKETING_STATUS_OPTIONS,
  ) => (
    <div style={{ marginBottom: 'var(--spacing-3)' }}>
      <label style={labelStyle}>{label}</label>
      <select
        style={inputStyle}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          ...pageCardStyle,
          width: '100%',
          maxWidth: 480,
          maxHeight: '90vh',
          overflowY: 'auto',
          position: 'relative',
        }}
      >
        <h3 style={{ margin: '0 0 var(--spacing-4)' }}>
          {customer ? 'Edit Customer' : 'Add Customer'}
        </h3>

        {field('Full Name *', 'full_name')}
        {field('Email', 'email', 'email')}
        {field('Mobile', 'mobile', 'tel')}
        {field('Phone (landline)', 'phone', 'tel')}

        <div
          style={{
            borderTop: '1px solid var(--color-border)',
            paddingTop: 'var(--spacing-3)',
            marginTop: 'var(--spacing-1)',
          }}
        >
          <p
            style={{
              margin: '0 0 var(--spacing-2)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-muted)',
            }}
          >
            Marketing preferences
          </p>
          {select('SMS Marketing', 'sms_marketing_status', MARKETING_STATUS_OPTIONS)}
          {select('Email Marketing', 'email_marketing_status', [
            ...MARKETING_STATUS_OPTIONS,
            { value: 'cleaned', label: 'Cleaned (bounced)' },
          ])}
        </div>

        {error && (
          <p
            style={{
              color: 'var(--color-danger)',
              fontSize: 'var(--font-size-sm)',
              marginBottom: 'var(--spacing-2)',
            }}
          >
            {error}
          </p>
        )}

        <div
          style={{
            display: 'flex',
            gap: 'var(--spacing-2)',
            justifyContent: 'flex-end',
            marginTop: 'var(--spacing-3)',
          }}
        >
          <button style={secondaryButtonStyle} onClick={onClose}>
            Cancel
          </button>
          <button
            style={{ ...primaryButtonStyle, opacity: saveMutation.isPending ? 0.6 : 1 }}
            disabled={saveMutation.isPending || !form.full_name.trim()}
            onClick={() => {
              setError('');
              saveMutation.mutate();
            }}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ContactsTab() {
  const [search, setSearch] = useState('');
  const [channel, setChannel] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(0);
  const [modal, setModal] = useState<{ open: boolean; customer: any | null }>({
    open: false,
    customer: null,
  });
  const limit = 25;

  const { data, isLoading } = useQuery({
    queryKey: ['messaging-contacts', search, channel, status, page],
    queryFn: () =>
      api.getContacts({
        search: search || undefined,
        channel: channel || undefined,
        status: status || undefined,
        limit,
        offset: page * limit,
      }),
  });

  const contacts = data?.contacts || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / limit);

  return (
    <div>
      {modal.open && (
        <CustomerModal
          customer={modal.customer}
          onClose={() => setModal({ open: false, customer: null })}
        />
      )}

      <div
        style={{
          display: 'flex',
          gap: 'var(--spacing-2)',
          marginBottom: 'var(--spacing-3)',
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <input
          style={{ ...inputStyle, maxWidth: 300 }}
          placeholder="Search by name, email, phone..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
        />
        <select
          style={{ ...inputStyle, maxWidth: 130 }}
          value={channel}
          onChange={(e) => {
            setChannel(e.target.value);
            setPage(0);
          }}
        >
          <option value="">All channels</option>
          <option value="sms">Has mobile</option>
          <option value="email">Has email</option>
        </select>
        <select
          style={{ ...inputStyle, maxWidth: 150 }}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(0);
          }}
        >
          <option value="">All statuses</option>
          <option value="subscribed">Subscribed</option>
          <option value="unsubscribed">Unsubscribed</option>
        </select>
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-muted)',
            lineHeight: '36px',
            flex: 1,
          }}
        >
          {total.toLocaleString()} customers
        </span>
        <button style={primaryButtonStyle} onClick={() => setModal({ open: true, customer: null })}>
          + Add Customer
        </button>
      </div>

      {isLoading ? (
        <div style={{ color: 'var(--color-text-muted)' }}>Loading customers...</div>
      ) : (
        <>
          {/* Mobile: stacked card list. See .claude/skills/ui-ux-admin/SKILL.md §2 */}
          <div className="resp-cards">
            {contacts.map((c: any) => (
              <div
                key={c.id ?? c.email ?? c.mobile}
                style={{
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-default)',
                  padding: 'var(--spacing-3)',
                  marginBottom: 'var(--spacing-2)',
                  backgroundColor: 'var(--color-surface)',
                }}
              >
                <div
                  style={{
                    fontWeight: 600,
                    fontSize: 'var(--font-size-md)',
                    marginBottom: 'var(--spacing-1)',
                  }}
                >
                  {c.full_name || '—'}
                </div>
                <div
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-muted)',
                    marginBottom: 'var(--spacing-2)',
                  }}
                >
                  {c.email || '—'}
                  {c.mobile ? ` · ${c.mobile}` : ''}
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 'var(--spacing-1)',
                    marginBottom: 'var(--spacing-2)',
                  }}
                >
                  <span
                    style={badgeStyle(
                      c.email_marketing_status === 'subscribed'
                        ? 'green'
                        : c.email_marketing_status === 'unsubscribed'
                          ? 'red'
                          : 'grey',
                    )}
                  >
                    📧 {c.email_marketing_status || 'n/a'}
                  </span>
                  <span
                    style={badgeStyle(
                      c.sms_marketing_status === 'subscribed'
                        ? 'green'
                        : c.sms_marketing_status === 'unsubscribed'
                          ? 'red'
                          : 'grey',
                    )}
                  >
                    💬 {c.sms_marketing_status || 'n/a'}
                  </span>
                  <span style={{ ...badgeStyle('grey') }}>📅 {c.appointment_count ?? 0} appts</span>
                </div>
                <div
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-muted)',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    rowGap: 4,
                    marginBottom: 'var(--spacing-2)',
                  }}
                >
                  <span>
                    Last:{' '}
                    <strong style={{ color: 'var(--color-text)' }}>
                      {c.last_booking_date ? fmtBookingDate(c.last_booking_date) : '—'}
                    </strong>
                  </span>
                  <span>
                    Next:{' '}
                    <strong style={{ color: 'var(--color-text)' }}>
                      {c.next_booking_date ? fmtBookingDate(c.next_booking_date) : '—'}
                    </strong>
                  </span>
                </div>
                {c.id != null && (
                  <button
                    style={{ ...smallButtonStyle, minHeight: 44, width: '100%' }}
                    onClick={() => setModal({ open: true, customer: c })}
                  >
                    Edit
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Desktop: HTML table */}
          <div className="resp-table" style={{ overflowX: 'auto' }}>
            <table
              style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
            >
              <thead>
                <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Mobile</th>
                  <th style={thStyle}>Email Status</th>
                  <th style={thStyle}>SMS Status</th>
                  <th style={thStyle}>Appointments</th>
                  <th style={thStyle}>Last Booking</th>
                  <th style={thStyle}>Next Booking</th>
                  <th style={thStyle}>Booked On</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {contacts.map((c: any) => (
                  <tr
                    key={c.id ?? c.email ?? c.mobile}
                    style={{ borderBottom: '1px solid var(--color-border)' }}
                  >
                    <td style={tdStyle}>{c.full_name || '—'}</td>
                    <td style={tdStyle}>{c.email || '—'}</td>
                    <td style={tdStyle}>{c.mobile || '—'}</td>
                    <td style={tdStyle}>
                      <span
                        style={badgeStyle(
                          c.email_marketing_status === 'subscribed'
                            ? 'green'
                            : c.email_marketing_status === 'unsubscribed'
                              ? 'red'
                              : 'grey',
                        )}
                      >
                        {c.email_marketing_status || 'n/a'}
                      </span>
                    </td>
                    <td style={tdStyle}>
                      <span
                        style={badgeStyle(
                          c.sms_marketing_status === 'subscribed'
                            ? 'green'
                            : c.sms_marketing_status === 'unsubscribed'
                              ? 'red'
                              : 'grey',
                        )}
                      >
                        {c.sms_marketing_status || 'n/a'}
                      </span>
                    </td>
                    <td style={tdStyle}>{c.appointment_count ?? 0}</td>
                    <td style={tdStyle}>
                      {c.last_booking_date ? fmtBookingDate(c.last_booking_date) : '—'}
                    </td>
                    <td style={tdStyle}>
                      {c.next_booking_date ? fmtBookingDate(c.next_booking_date) : '—'}
                    </td>
                    <td style={tdStyle}>
                      {c.next_booking_created_at ? fmtBookingDate(c.next_booking_created_at) : '—'}
                    </td>
                    <td style={tdStyle}>
                      {c.id != null && (
                        <button
                          style={{ ...smallButtonStyle, padding: '2px 10px' }}
                          onClick={() => setModal({ open: true, customer: c })}
                        >
                          Edit
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div
              style={{
                display: 'flex',
                gap: 'var(--spacing-2)',
                alignItems: 'center',
                marginTop: 'var(--spacing-3)',
                justifyContent: 'center',
              }}
            >
              <button
                style={smallButtonStyle}
                disabled={page === 0}
                onClick={() => setPage(page - 1)}
              >
                ‹ Prev
              </button>
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                Page {page + 1} of {totalPages}
              </span>
              <button
                style={smallButtonStyle}
                disabled={page >= totalPages - 1}
                onClick={() => setPage(page + 1)}
              >
                Next ›
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: 'var(--spacing-2)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--color-text-muted)',
};

const tdStyle: React.CSSProperties = {
  padding: 'var(--spacing-2)',
  verticalAlign: 'middle',
};

// ─── Send Message Tab ──────────────────────────────────────────────

function SendTab() {
  const [mode, setMode] = useState<'template' | 'adhoc'>('template');
  const [templateId, setTemplateId] = useState<number | null>(null);
  // Ad-hoc free-text fields
  const [adhocChannel, setAdhocChannel] = useState<'sms' | 'email'>('sms');
  const [adhocSubject, setAdhocSubject] = useState('');
  const [adhocBody, setAdhocBody] = useState('');
  const [contactSearch, setContactSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedContact, setSelectedContact] = useState<any | null>(null);
  const [variables, setVariables] = useState<Record<string, string>>({});
  const [previewData, setPreviewData] = useState<any | null>(null);
  const [sendResult, setSendResult] = useState<any | null>(null);
  const [confirmSend, setConfirmSend] = useState(false);

  // Debounce contact search — wait 300ms after typing stops before querying
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(contactSearch), 300);
    return () => clearTimeout(t);
  }, [contactSearch]);

  const { data: templates = [] } = useQuery({
    queryKey: ['messaging-templates'],
    queryFn: () => api.getTemplates(),
  });

  const selectedTemplate = templates.find((t: any) => t.id === templateId);
  const activeChannel: 'sms' | 'email' | undefined =
    mode === 'adhoc' ? adhocChannel : selectedTemplate?.channel;

  const { data: contactResults, isLoading: loadingContacts } = useQuery({
    queryKey: ['messaging-contacts', debouncedSearch, activeChannel],
    queryFn: () =>
      api.getContacts({
        search: debouncedSearch,
        channel: activeChannel || undefined,
        limit: 10,
      }),
    enabled: debouncedSearch.length >= 2,
  });

  const { data: config } = useQuery({
    queryKey: ['messaging-config'],
    queryFn: () => api.getMessagingConfig(),
  });

  const previewMutation = useMutation({
    mutationFn: () => {
      // Pass the already-fetched contact fields as variable overrides so that
      // booking-only contacts (id = null) still get their name/email resolved,
      // and any caller-supplied variables take final precedence.
      const contactVars: Record<string, string> = selectedContact
        ? {
            first_name:
              selectedContact.first_name || selectedContact.full_name?.split(' ')[0] || 'there',
            last_name: selectedContact.last_name || '',
            full_name: selectedContact.full_name || '',
            email: selectedContact.email || '',
            mobile: selectedContact.mobile || '',
          }
        : {};
      return api.previewMessage({
        templateId: templateId!,
        customerId: selectedContact?.id ?? undefined,
        variables: { ...contactVars, ...variables },
      });
    },
    onSuccess: (data) => setPreviewData(data),
  });

  const sendMutation = useMutation({
    mutationFn: () => {
      const c = selectedContact!;
      const contactPayload =
        c.id == null
          ? {
              full_name: c.full_name,
              first_name: c.first_name,
              last_name: c.last_name,
              email: c.email,
              mobile: c.mobile,
            }
          : undefined;

      if (mode === 'adhoc') {
        return api.sendMessage({
          adhoc: {
            channel: adhocChannel,
            subject: adhocChannel === 'email' ? adhocSubject : undefined,
            body: adhocBody,
          },
          customerId: c.id ?? undefined,
          contact: contactPayload,
        });
      }

      return api.sendMessage({
        templateId: templateId!,
        customerId: c.id ?? undefined,
        contact: contactPayload,
        variables,
      });
    },
    onSuccess: (data) => setSendResult(data),
  });

  const contacts = contactResults?.contacts || [];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-4)' }}>
      {/* Left column: Configure */}
      <div>
        <div style={pageCardStyle}>
          <h3 style={{ margin: '0 0 var(--spacing-3)', fontWeight: 600 }}>Compose Message</h3>

          {/* Provider status */}
          {config && (
            <div
              style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-3)' }}
            >
              <span style={badgeStyle(config.gmail?.configured ? 'green' : 'red')}>
                Email (Gmail): {config.gmail?.configured ? 'Ready' : 'Not configured'}
              </span>
              <span style={badgeStyle(config.twilio?.configured ? 'green' : 'red')}>
                SMS (Twilio): {config.twilio?.configured ? 'Ready' : 'Not configured'}
              </span>
            </div>
          )}

          {/* Mode toggle */}
          <div
            style={{ marginBottom: 'var(--spacing-3)', display: 'flex', gap: 'var(--spacing-1)' }}
          >
            <button
              style={{
                ...smallButtonStyle,
                ...(mode === 'template'
                  ? {
                      backgroundColor: 'var(--color-accent)',
                      color: 'var(--color-text-inverse)',
                      borderColor: 'var(--color-accent)',
                    }
                  : {}),
              }}
              onClick={() => {
                setMode('template');
                setPreviewData(null);
                setSendResult(null);
              }}
            >
              📄 Use template
            </button>
            <button
              style={{
                ...smallButtonStyle,
                ...(mode === 'adhoc'
                  ? {
                      backgroundColor: 'var(--color-accent)',
                      color: 'var(--color-text-inverse)',
                      borderColor: 'var(--color-accent)',
                    }
                  : {}),
              }}
              onClick={() => {
                setMode('adhoc');
                setPreviewData(null);
                setSendResult(null);
              }}
            >
              ✏️ Free text
            </button>
          </div>

          {/* Step 1a: Select template (template mode) */}
          {mode === 'template' && (
            <div style={{ marginBottom: 'var(--spacing-3)' }}>
              <label style={labelStyle}>1. Select Template</label>
              <select
                style={inputStyle}
                value={templateId || ''}
                onChange={(e) => {
                  setTemplateId(e.target.value ? Number(e.target.value) : null);
                  setPreviewData(null);
                  setSendResult(null);
                }}
              >
                <option value="">Choose a template...</option>
                {templates.map((t: any) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.channel.toUpperCase()})
                  </option>
                ))}
              </select>
              {selectedTemplate && (
                <div
                  style={{
                    marginTop: 4,
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-muted)',
                  }}
                >
                  Channel: {selectedTemplate.channel.toUpperCase()} | Category:{' '}
                  {selectedTemplate.category || 'None'}
                  {selectedTemplate.variables?.length > 0 && (
                    <>
                      {' '}
                      | Variables:{' '}
                      {selectedTemplate.variables.map((v: string) => `{${v}}`).join(', ')}
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 1b: Free text (adhoc mode) */}
          {mode === 'adhoc' && (
            <div style={{ marginBottom: 'var(--spacing-3)' }}>
              <label style={labelStyle}>1. Channel</label>
              <select
                style={inputStyle}
                value={adhocChannel}
                onChange={(e) => {
                  setAdhocChannel(e.target.value as 'sms' | 'email');
                  setSelectedContact(null);
                  setContactSearch('');
                  setPreviewData(null);
                  setSendResult(null);
                }}
              >
                <option value="sms">SMS</option>
                <option value="email">Email</option>
              </select>
              {adhocChannel === 'email' && (
                <div style={{ marginTop: 'var(--spacing-2)' }}>
                  <label style={labelStyle}>Subject</label>
                  <input
                    style={inputStyle}
                    placeholder="Email subject"
                    value={adhocSubject}
                    onChange={(e) => {
                      setAdhocSubject(e.target.value);
                      setPreviewData(null);
                    }}
                  />
                </div>
              )}
              <div style={{ marginTop: 'var(--spacing-2)' }}>
                <label style={labelStyle}>
                  Message{' '}
                  <span
                    style={{
                      fontWeight: 400,
                      color:
                        adhocChannel === 'sms' && adhocBody.length > 160
                          ? '#dc3545'
                          : 'var(--color-text-muted)',
                      fontSize: '11px',
                      textTransform: 'none',
                    }}
                  >
                    ({adhocBody.length} chars
                    {adhocChannel === 'sms' && adhocBody.length > 160 ? ' — over SMS limit' : ''})
                  </span>
                </label>
                <textarea
                  style={{
                    ...inputStyle,
                    minHeight: 100,
                    resize: 'vertical',
                    fontFamily: 'inherit',
                  }}
                  placeholder={
                    adhocChannel === 'sms'
                      ? "Type your SMS here. No templates, no variable substitution — what you type is what's sent."
                      : 'Type your email body here. Plain text only.'
                  }
                  value={adhocBody}
                  onChange={(e) => {
                    setAdhocBody(e.target.value);
                    setPreviewData(null);
                  }}
                />
              </div>
              <div
                style={{
                  marginTop: 4,
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-muted)',
                }}
              >
                No variables. Opt-outs and provider checks still apply.
              </div>
            </div>
          )}

          {/* Step 2: Select contact */}
          <div style={{ marginBottom: 'var(--spacing-3)' }}>
            <label style={labelStyle}>2. Select Contact</label>
            <input
              style={inputStyle}
              placeholder="Search contacts by name, email, phone..."
              value={contactSearch}
              onChange={(e) => {
                setContactSearch(e.target.value);
                setSelectedContact(null);
                setPreviewData(null);
                setSendResult(null);
              }}
            />
            {debouncedSearch.length >= 2 && !selectedContact && (
              <div
                style={{
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-default)',
                  marginTop: 4,
                  maxHeight: 200,
                  overflowY: 'auto',
                  backgroundColor: 'var(--color-surface)',
                }}
              >
                {contactSearch !== debouncedSearch || loadingContacts ? (
                  <div
                    style={{
                      padding: 'var(--spacing-2)',
                      color: 'var(--color-text-muted)',
                      fontSize: 'var(--font-size-sm)',
                    }}
                  >
                    Searching...
                  </div>
                ) : contacts.length === 0 ? (
                  <div
                    style={{
                      padding: 'var(--spacing-2)',
                      color: 'var(--color-text-muted)',
                      fontSize: 'var(--font-size-sm)',
                    }}
                  >
                    No contacts found
                  </div>
                ) : (
                  contacts.map((c: any) => (
                    <div
                      key={c.id ?? `${c.email}-${c.mobile}`}
                      style={{
                        padding: 'var(--spacing-2)',
                        cursor: 'pointer',
                        borderBottom: '1px solid var(--color-border)',
                        fontSize: 'var(--font-size-sm)',
                      }}
                      onClick={() => {
                        setSelectedContact(c);
                        setContactSearch(c.full_name || c.email || c.mobile);
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.backgroundColor = 'var(--color-surface-elevated)')
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.backgroundColor = 'var(--color-surface)')
                      }
                    >
                      <strong>{c.full_name || 'Unknown'}</strong>
                      {c.email && (
                        <span style={{ color: 'var(--color-text-muted)' }}> • {c.email}</span>
                      )}
                      {c.mobile && (
                        <span style={{ color: 'var(--color-text-muted)' }}> • {c.mobile}</span>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
            {selectedContact && (
              <div
                style={{
                  marginTop: 8,
                  padding: 'var(--spacing-2)',
                  backgroundColor: 'var(--color-surface-elevated)',
                  borderRadius: 'var(--radius-default)',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                <div>
                  <strong>{selectedContact.full_name || 'Unknown'}</strong>
                </div>
                <div style={{ color: 'var(--color-text-muted)' }}>
                  {selectedContact.email && (
                    <>
                      Email: {selectedContact.email} ({selectedContact.email_marketing_status})
                      <br />
                    </>
                  )}
                  {selectedContact.mobile && (
                    <>
                      Mobile: {selectedContact.mobile} ({selectedContact.sms_marketing_status})
                    </>
                  )}
                </div>
                <button
                  style={{ ...smallButtonStyle, marginTop: 4, fontSize: '11px' }}
                  onClick={() => {
                    setSelectedContact(null);
                    setContactSearch('');
                  }}
                >
                  Change contact
                </button>
              </div>
            )}
          </div>

          {/* Step 3: Optional variable overrides (template mode only) */}
          {mode === 'template' && selectedTemplate?.variables?.length > 0 && (
            <div style={{ marginBottom: 'var(--spacing-3)' }}>
              <label style={labelStyle}>3. Custom Variables (optional)</label>
              <div style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
                {/* rebook_url — auto from customer's last location, or pick a specific link */}
                {selectedTemplate.variables.includes('rebook_url') && (
                  <div>
                    <label
                      style={{ ...labelStyle, textTransform: 'none' as const, fontSize: '12px' }}
                    >
                      {`{rebook_url}`}
                    </label>
                    <RebookUrlSelect
                      value={variables['rebook_url'] || ''}
                      onChange={(url) => setVariables({ ...variables, rebook_url: url })}
                    />
                  </div>
                )}
                {selectedTemplate.variables
                  .filter(
                    (v: string) =>
                      ![
                        'first_name',
                        'last_name',
                        'full_name',
                        'email',
                        'mobile',
                        'location_name',
                        'rebook_url',
                      ].includes(v),
                  )
                  .map((v: string) => (
                    <div key={v}>
                      <label
                        style={{ ...labelStyle, textTransform: 'none' as const, fontSize: '12px' }}
                      >{`{${v}}`}</label>
                      <input
                        style={inputStyle}
                        placeholder={`Value for {${v}}`}
                        value={variables[v] || ''}
                        onChange={(e) => setVariables({ ...variables, [v]: e.target.value })}
                      />
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
            {/* Preview is only useful for template mode (variable substitution). */}
            {mode === 'template' && (
              <button
                style={{ ...secondaryButtonStyle, opacity: templateId ? 1 : 0.5 }}
                disabled={!templateId}
                onClick={() => previewMutation.mutate()}
              >
                {previewMutation.isPending ? 'Loading...' : '👁 Preview'}
              </button>
            )}
            {(() => {
              const canSend =
                !!selectedContact &&
                !sendMutation.isPending &&
                (mode === 'adhoc' ? adhocBody.trim().length > 0 : !!templateId);
              const channelLabel =
                mode === 'adhoc'
                  ? adhocChannel.toUpperCase()
                  : selectedTemplate?.channel?.toUpperCase() || '';
              return (
                <button
                  style={{ ...primaryButtonStyle, opacity: canSend ? 1 : 0.5 }}
                  disabled={!canSend}
                  onClick={() => setConfirmSend(true)}
                >
                  {sendMutation.isPending ? 'Sending...' : `📤 Send ${channelLabel}`}
                </button>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Right column: Preview & Result */}
      <div>
        {previewData && (
          <div style={{ ...pageCardStyle, marginBottom: 'var(--spacing-3)' }}>
            <h3 style={{ margin: '0 0 var(--spacing-2)', fontWeight: 600 }}>Preview</h3>
            {previewData.subject && (
              <div style={{ marginBottom: 'var(--spacing-2)' }}>
                <label style={labelStyle}>Subject</label>
                <div style={{ ...inputStyle, backgroundColor: 'var(--color-surface-elevated)' }}>
                  {previewData.subject}
                </div>
              </div>
            )}
            <label style={labelStyle}>Body</label>
            <div
              style={{
                padding: 'var(--spacing-3)',
                backgroundColor: 'var(--color-surface-elevated)',
                borderRadius: 'var(--radius-default)',
                whiteSpace: 'pre-wrap',
                fontSize: 'var(--font-size-sm)',
                lineHeight: 1.6,
                border: '1px solid var(--color-border)',
              }}
            >
              {previewData.body}
            </div>
            {previewData.variables && (
              <div
                style={{
                  marginTop: 'var(--spacing-2)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-muted)',
                }}
              >
                Variables used:{' '}
                {Object.entries(previewData.variables)
                  .map(([k, v]) => `${k}="${v}"`)
                  .join(', ')}
              </div>
            )}
          </div>
        )}

        {sendResult && (
          <div
            style={{
              ...pageCardStyle,
              borderColor:
                sendResult.status === 'sent'
                  ? 'rgba(40,167,69,0.5)'
                  : sendResult.status === 'skipped'
                    ? 'rgba(255,193,7,0.5)'
                    : 'rgba(220,53,69,0.5)',
            }}
          >
            <h3 style={{ margin: '0 0 var(--spacing-2)', fontWeight: 600 }}>
              {sendResult.status === 'sent'
                ? '✅ Message Sent'
                : sendResult.status === 'skipped'
                  ? '⚠️ Message Skipped'
                  : '❌ Send Failed'}
            </h3>
            <div style={{ fontSize: 'var(--font-size-sm)' }}>
              <div>Channel: {sendResult.channel?.toUpperCase()}</div>
              <div>Recipient: {sendResult.recipient}</div>
              <div>Status: {sendResult.status}</div>
              {sendResult.providerMessageId && (
                <div>Provider ID: {sendResult.providerMessageId}</div>
              )}
              {sendResult.error && (
                <div style={{ color: 'var(--color-error)', marginTop: 4 }}>
                  Error: {sendResult.error}
                </div>
              )}
            </div>
          </div>
        )}

        {!previewData && !sendResult && (
          <div style={{ ...pageCardStyle, textAlign: 'center', color: 'var(--color-text-muted)' }}>
            <p>
              {mode === 'template'
                ? 'Select a template and contact, then click Preview or Send.'
                : 'Type your message, pick a contact, then click Send.'}
            </p>
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={confirmSend}
        title={`Send ${activeChannel?.toUpperCase() || 'message'}?`}
        message={`This will send a ${activeChannel || 'message'}${mode === 'adhoc' ? ' (free text)' : ''} to ${selectedContact?.full_name || selectedContact?.email || selectedContact?.mobile || 'the selected contact'}.`}
        confirmLabel="Send"
        tone="warning"
        onConfirm={() => {
          setConfirmSend(false);
          sendMutation.mutate();
        }}
        onCancel={() => setConfirmSend(false)}
      />
    </div>
  );
}

// ─── Send Log Tab ──────────────────────────────────────────────────

function SendLogTab() {
  const [channel, setChannel] = useState('');
  const [status, setStatus] = useState('');
  const [sentBy, setSentBy] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(0);
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const limit = 25;

  const { data, isLoading } = useQuery({
    queryKey: ['messaging-send-log', channel, status, sentBy, dateFrom, dateTo, page],
    queryFn: () =>
      api.getSendLog({
        channel: channel || undefined,
        status: status || undefined,
        sentBy: sentBy || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        limit,
        offset: page * limit,
      }),
  });

  const logs = data?.logs || [];
  const total = data?.total || 0;
  const totalPages = Math.ceil(total / limit);

  return (
    <div>
      <div
        style={{
          display: 'flex',
          gap: 'var(--spacing-2)',
          marginBottom: 'var(--spacing-3)',
          alignItems: 'center',
        }}
      >
        <select
          style={{ ...inputStyle, maxWidth: 150 }}
          value={channel}
          onChange={(e) => {
            setChannel(e.target.value);
            setPage(0);
          }}
        >
          <option value="">All channels</option>
          <option value="sms">SMS</option>
          <option value="email">Email</option>
        </select>
        <select
          style={{ ...inputStyle, maxWidth: 150 }}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(0);
          }}
        >
          <option value="">All statuses</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
          <option value="skipped">Skipped</option>
        </select>
        <select
          style={{ ...inputStyle, maxWidth: 200 }}
          value={sentBy}
          onChange={(e) => {
            setSentBy(e.target.value);
            setPage(0);
          }}
        >
          <option value="">All senders</option>
          <option value="system">system</option>
          <option value="system:rebooking-daily">system:rebooking-daily</option>
          <option value="system:booking-reminder">system:booking-reminder</option>
          <option value="admin@bling-bling-brisbane.com.au">admin</option>
        </select>
        <input
          type="date"
          style={{ ...inputStyle, maxWidth: 160 }}
          value={dateFrom}
          onChange={(e) => {
            setDateFrom(e.target.value);
            setPage(0);
          }}
          placeholder="From"
          title="From date"
        />
        <input
          type="date"
          style={{ ...inputStyle, maxWidth: 160 }}
          value={dateTo}
          onChange={(e) => {
            setDateTo(e.target.value);
            setPage(0);
          }}
          placeholder="To"
          title="To date"
        />
        {(channel || status || sentBy || dateFrom || dateTo) && (
          <button
            style={{
              ...smallButtonStyle,
              fontSize: 'var(--font-size-sm)',
            }}
            onClick={() => {
              setChannel('');
              setStatus('');
              setSentBy('');
              setDateFrom('');
              setDateTo('');
              setPage(0);
            }}
          >
            Clear filters
          </button>
        )}
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
          {total} messages sent
        </span>
      </div>

      {isLoading ? (
        <div style={{ color: 'var(--color-text-muted)' }}>Loading send log...</div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table
              style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
            >
              <thead>
                <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Template</th>
                  <th style={thStyle}>Contact</th>
                  <th style={thStyle}>Channel</th>
                  <th style={thStyle}>Recipient</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Sent By</th>
                </tr>
              </thead>
              <tbody>
                {logs.flatMap((l: any) => [
                  <tr
                    key={l.id}
                    style={{
                      borderBottom: expandedId === l.id ? 'none' : '1px solid var(--color-border)',
                      cursor: 'pointer',
                      backgroundColor: expandedId === l.id ? 'rgba(217, 178, 86, 0.06)' : undefined,
                    }}
                    onClick={() => setExpandedId(expandedId === l.id ? null : l.id)}
                    title="Click to view message body"
                  >
                    <td style={tdStyle}>
                      {new Date(l.sent_at).toLocaleString('en-AU', {
                        timeZone: 'Australia/Brisbane',
                      })}
                    </td>
                    <td style={tdStyle}>
                      {l.template_name || '—'}
                      {Array.isArray(l.attachments) && l.attachments.length > 0 && (
                        <span title={`${l.attachments.length} attachment(s)`}> 📎</span>
                      )}
                    </td>
                    <td style={tdStyle}>{l.customer_name || '—'}</td>
                    <td style={tdStyle}>
                      <span style={badgeStyle(l.channel === 'sms' ? 'gold' : 'green')}>
                        {l.channel.toUpperCase()}
                      </span>
                    </td>
                    <td style={tdStyle}>{l.recipient}</td>
                    <td style={tdStyle}>
                      <span
                        style={badgeStyle(
                          l.status === 'sent' ? 'green' : l.status === 'failed' ? 'red' : 'grey',
                        )}
                      >
                        {l.status}
                      </span>
                    </td>
                    <td style={tdStyle}>{l.sent_by || '—'}</td>
                  </tr>,
                  ...(expandedId === l.id
                    ? [
                        <tr
                          key={`${l.id}-body`}
                          style={{ borderBottom: '1px solid var(--color-border)' }}
                        >
                          <td colSpan={7} style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}>
                            <div
                              style={{
                                background: 'var(--color-background-alt, rgba(0,0,0,0.15))',
                                borderRadius: 'var(--border-radius, 6px)',
                                padding: 'var(--spacing-3)',
                                fontSize: 'var(--font-size-sm)',
                                whiteSpace: 'pre-wrap',
                                lineHeight: 1.5,
                                color: 'var(--color-text)',
                              }}
                            >
                              <div
                                style={{
                                  fontWeight: 600,
                                  marginBottom: 'var(--spacing-1)',
                                  color: 'var(--color-accent, #D9B256)',
                                }}
                              >
                                Message Body
                              </div>
                              {l.body || '(no body)'}
                              {Array.isArray(l.attachments) && l.attachments.length > 0 && (
                                <div style={{ marginTop: 'var(--spacing-2)' }}>
                                  <strong>📎 Attachments:</strong>
                                  <ul style={{ margin: '4px 0 0', paddingLeft: '20px' }}>
                                    {l.attachments.map((a: any, i: number) => (
                                      <li key={i}>
                                        {a.filename}
                                        {typeof a.bytes === 'number'
                                          ? ` (${(a.bytes / 1024).toFixed(0)} KB)`
                                          : ''}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {l.error_message && (
                                <div style={{ marginTop: 'var(--spacing-2)', color: '#e74c3c' }}>
                                  <strong>Error:</strong> {l.error_message}
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>,
                      ]
                    : []),
                ])}
              </tbody>
            </table>
          </div>

          {logs.length === 0 && (
            <div
              style={{
                color: 'var(--color-text-muted)',
                textAlign: 'center',
                padding: 'var(--spacing-4)',
              }}
            >
              No messages sent yet.
            </div>
          )}

          {totalPages > 1 && (
            <div
              style={{
                display: 'flex',
                gap: 'var(--spacing-2)',
                alignItems: 'center',
                marginTop: 'var(--spacing-3)',
                justifyContent: 'center',
              }}
            >
              <button
                style={smallButtonStyle}
                disabled={page === 0}
                onClick={() => setPage(page - 1)}
              >
                ‹ Prev
              </button>
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                Page {page + 1} of {totalPages}
              </span>
              <button
                style={smallButtonStyle}
                disabled={page >= totalPages - 1}
                onClick={() => setPage(page + 1)}
              >
                Next ›
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Scheduled Tab ─────────────────────────────────────────────────

/** Format a booking date as "Fri, 17 Apr at 4:00PM" (Brisbane). */
function fmtBookingDate(dateStr: string): string {
  const d = new Date(dateStr);
  const dayName = d.toLocaleDateString('en-AU', {
    weekday: 'short',
    timeZone: 'Australia/Brisbane',
  });
  const day = d.toLocaleDateString('en-AU', { day: 'numeric', timeZone: 'Australia/Brisbane' });
  const month = d.toLocaleDateString('en-AU', { month: 'short', timeZone: 'Australia/Brisbane' });
  const time = d
    .toLocaleTimeString('en-AU', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Australia/Brisbane',
    })
    .replace(/\s/g, '')
    .toUpperCase();
  return `${dayName}, ${day} ${month} at ${time}`;
}

function fmtScheduled(dateStr: string, status?: string): string {
  const d = new Date(dateStr);
  const base = d.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
  // Relative labels ("overdue", "due now", "in X min") only make sense while a
  // message is still pending — a sent/failed/cancelled row is not "overdue".
  if (status && status !== 'pending') return base;
  const diffMin = Math.round((d.getTime() - Date.now()) / 60000);
  if (diffMin < 0) return `${base} (overdue)`;
  if (diffMin === 0) return `${base} (due now)`;
  if (diffMin < 60) return `${base} (in ${diffMin} min)`;
  return base;
}

function ScheduleForm({ onCreated }: { onCreated: () => void }) {
  const qc = useQueryClient();
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [contactSearch, setContactSearch] = useState('');
  const [selectedContact, setSelectedContact] = useState<any | null>(null);
  const [scheduledFor, setScheduledFor] = useState('');
  const [formError, setFormError] = useState('');

  const { data: allTemplates = [] } = useQuery({
    queryKey: ['messaging-templates'],
    queryFn: () => api.getTemplates(),
  });

  const emailTemplates = (allTemplates as any[]).filter((t: any) => t.channel === 'email');
  const smsTemplates = (allTemplates as any[]).filter((t: any) => t.channel === 'sms');
  const selectedTemplate = (allTemplates as any[]).find((t: any) => t.id === templateId);
  const selectedChannel: 'email' | 'sms' | null = selectedTemplate?.channel ?? null;

  // Reset the contact whenever switching between SMS and email templates, since a
  // contact valid for one channel may not have the required field for the other.
  useEffect(() => {
    setSelectedContact(null);
    setContactSearch('');
  }, [selectedChannel]);

  const { data: contactResults } = useQuery({
    queryKey: ['schedule-contacts', contactSearch, selectedChannel],
    queryFn: () =>
      api.getContacts({ search: contactSearch, channel: selectedChannel ?? 'email', limit: 10 }),
    enabled: contactSearch.length >= 2,
  });

  const contacts: any[] = (contactResults as any)?.contacts || [];

  const scheduleMutation = useMutation({
    mutationFn: () =>
      api.scheduleMessage({
        templateId: templateId!,
        customerId: selectedContact!.id,
        scheduledFor,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['scheduled-messages'] });
      onCreated();
    },
    onError: (err: any) => setFormError(err.message),
  });

  const minDateTime = new Date(Date.now() + 60000)
    .toLocaleString('sv-SE', { timeZone: 'Australia/Brisbane' })
    .slice(0, 16);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError('');
    if (!templateId) {
      setFormError('Select a template');
      return;
    }
    if (!selectedContact) {
      setFormError('Select a contact');
      return;
    }
    if (!scheduledFor) {
      setFormError('Set a scheduled time');
      return;
    }
    if (new Date(scheduledFor) <= new Date()) {
      setFormError('Scheduled time must be in the future');
      return;
    }
    scheduleMutation.mutate();
  }

  return (
    <div style={{ ...pageCardStyle, marginBottom: 'var(--spacing-4)' }}>
      <h3 style={{ margin: '0 0 var(--spacing-3)', fontWeight: 600 }}>
        Schedule {selectedChannel === 'sms' ? 'SMS' : 'Email'}
      </h3>
      <form onSubmit={handleSubmit}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 'var(--spacing-3)',
            marginBottom: 'var(--spacing-3)',
          }}
        >
          <div>
            <label style={labelStyle}>Template</label>
            <select
              style={inputStyle}
              value={templateId || ''}
              onChange={(e) => setTemplateId(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">Choose template...</option>
              {emailTemplates.length > 0 && (
                <optgroup label="Email">
                  {emailTemplates.map((t: any) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </optgroup>
              )}
              {smsTemplates.length > 0 && (
                <optgroup label="SMS">
                  {smsTemplates.map((t: any) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>

          <div>
            <label style={labelStyle}>Contact</label>
            {selectedContact ? (
              <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center' }}>
                <span style={{ fontSize: 'var(--font-size-sm)', flex: 1 }}>
                  {selectedContact.full_name} —{' '}
                  {selectedChannel === 'sms' ? selectedContact.mobile : selectedContact.email}
                </span>
                <button
                  type="button"
                  style={smallButtonStyle}
                  onClick={() => setSelectedContact(null)}
                >
                  ×
                </button>
              </div>
            ) : (
              <>
                <input
                  style={inputStyle}
                  placeholder="Search by name or email..."
                  value={contactSearch}
                  onChange={(e) => setContactSearch(e.target.value)}
                />
                {contacts.length > 0 && (
                  <div
                    style={{
                      border: '1px solid var(--color-border)',
                      borderRadius: 'var(--radius-default)',
                      maxHeight: 160,
                      overflowY: 'auto',
                      marginTop: 2,
                      backgroundColor: 'var(--color-surface)',
                    }}
                  >
                    {contacts.map((c: any) => (
                      <div
                        key={c.id}
                        style={{
                          padding: 'var(--spacing-2) var(--spacing-3)',
                          cursor: 'pointer',
                          fontSize: 'var(--font-size-sm)',
                          borderBottom: '1px solid var(--color-border)',
                        }}
                        onClick={() => {
                          setSelectedContact(c);
                          setContactSearch('');
                        }}
                      >
                        {c.full_name} — {selectedChannel === 'sms' ? c.mobile : c.email}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          <div>
            <label style={labelStyle}>Scheduled For</label>
            <input
              type="datetime-local"
              style={inputStyle}
              min={minDateTime}
              value={scheduledFor}
              onChange={(e) => setScheduledFor(e.target.value)}
            />
          </div>
        </div>

        {formError && (
          <p
            style={{
              color: '#dc3545',
              fontSize: 'var(--font-size-sm)',
              margin: '0 0 var(--spacing-2)',
            }}
          >
            {formError}
          </p>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="submit" style={primaryButtonStyle} disabled={scheduleMutation.isPending}>
            {scheduleMutation.isPending
              ? 'Scheduling...'
              : selectedChannel === 'sms'
                ? 'Schedule SMS'
                : 'Schedule Email'}
          </button>
        </div>
      </form>
    </div>
  );
}

/** Upcoming automatic "on the way" reminders — jobs in the next 48h that will
 *  get an arrival text/email ~30 min before they start. These fire automatically
 *  (they aren't queued messages), so this shows them so nothing's a surprise. */
function ArrivalReminderPreview() {
  const qc = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const [pendingCancel, setPendingCancel] = useState<{ eventId: string; name: string } | null>(null);
  const [undo, setUndo] = useState<{ eventId: string; name: string } | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ['arrival-reminders-preview'],
    queryFn: () => api.getArrivalRemindersPreview(336),
    refetchInterval: 60_000,
  });
  const cancelMut = useMutation({
    mutationFn: (v: { eventId: string; name: string }) => api.cancelArrivalReminder(v.eventId).then(() => v),
    onSuccess: (v) => {
      setPendingCancel(null);
      setUndo(v);
      qc.invalidateQueries({ queryKey: ['arrival-reminders-preview'] });
    },
  });
  const restoreMut = useMutation({
    mutationFn: (eventId: string) => api.restoreArrivalReminder(eventId),
    onSuccess: () => {
      setUndo(null);
      qc.invalidateQueries({ queryKey: ['arrival-reminders-preview'] });
    },
  });

  if (isLoading || !data) return null;
  const jobs = (data.results ?? []).filter((r) => new Date(r.start).getTime() > Date.now());
  if (jobs.length === 0 && !undo) return null;

  const fmt = (iso: string) =>
    new Date(iso).toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', timeZone: 'Australia/Brisbane' });
  // The reminder fires ~30 min before the job starts.
  const sendTime = (iso: string) => fmt(new Date(new Date(iso).getTime() - 30 * 60000).toISOString());
  const cleanTitle = (t: string | null) =>
    (t ?? '').replace(/\b(quote|invoice)\s+(qu|inv)-?\d+/gi, '').replace(/^[\s-–]+/, '').replace(/\s{2,}/g, ' ').trim() || 'Job';

  return (
    <div style={{ ...pageCardStyle, marginBottom: 'var(--spacing-3)', borderLeft: '3px solid var(--color-accent)' }}>
      <h3 style={{ margin: 0, fontSize: 'var(--font-size-md)', fontWeight: 600 }}>🚗 Arrival reminders — upcoming jobs</h3>
      <p style={{ margin: '4px 0 var(--spacing-2)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
        Sent automatically ~30 min before each job starts (these aren’t queued messages, so they don’t appear in the list below). Click a row to see the exact message.
      </p>
      {undo && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--spacing-2)', background: 'var(--color-surface-elevated)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 'var(--spacing-2) var(--spacing-3)', marginBottom: 'var(--spacing-2)' }}>
          <span style={{ fontSize: 'var(--font-size-sm)' }}>Cancelled the arrival reminder for <strong>{undo.name}</strong> — it won’t be sent.</span>
          <button style={{ ...smallButtonStyle }} disabled={restoreMut.isPending} onClick={() => restoreMut.mutate(undo.eventId)}>Undo</button>
        </div>
      )}
      {jobs.length > 0 && (
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
            {['Job', 'Job starts', 'Sends (~30 min before)', 'Channel', 'Goes to', '', ''].map((h, i) => (
              <th key={i} style={{ textAlign: 'left', padding: '4px 8px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {jobs.flatMap((r) => {
            const chan = r.channel.startsWith('sms') ? 'SMS' : r.channel.startsWith('email') ? 'Email' : 'No contact';
            const isOpen = openId === r.eventId;
            const rows = [
              <tr key={r.eventId} style={{ borderBottom: isOpen ? 'none' : '1px solid var(--color-border)', cursor: 'pointer' }} onClick={() => setOpenId(isOpen ? null : r.eventId)}>
                <td style={{ padding: '6px 8px' }}>{cleanTitle(r.title)}</td>
                <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{fmt(r.start)}</td>
                <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', fontWeight: 600 }}>{sendTime(r.start)}</td>
                <td style={{ padding: '6px 8px' }}>
                  {chan === 'No contact'
                    ? <span style={{ color: 'var(--color-error)', fontWeight: 600 }}>No contact ⚠</span>
                    : <span style={badgeStyle(chan === 'SMS' ? 'gold' : 'green')}>{chan}</span>}
                </td>
                <td style={{ padding: '6px 8px', color: 'var(--color-text-muted)' }}>{r.contact || '—'}</td>
                <td style={{ padding: '6px 8px' }}>
                  <button
                    style={{ ...smallButtonStyle, color: 'var(--color-error)', borderColor: 'var(--color-error)' }}
                    onClick={(e) => { e.stopPropagation(); setPendingCancel({ eventId: r.eventId, name: cleanTitle(r.title) }); }}
                  >
                    Cancel
                  </button>
                </td>
                <td style={{ padding: '6px 8px', color: 'var(--color-text-muted)' }}>{isOpen ? '▾' : '▸'}</td>
              </tr>,
            ];
            if (isOpen) {
              rows.push(
                <tr key={`${r.eventId}-msg`} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td colSpan={7} style={{ padding: '0 8px 10px' }}>
                    <div style={{ background: 'var(--color-surface-elevated)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 'var(--spacing-2) var(--spacing-3)', whiteSpace: 'pre-wrap', fontSize: 'var(--font-size-sm)' }}>
                      {r.body || <span style={{ color: 'var(--color-text-muted)' }}>No contact on this job, so no message will be sent.</span>}
                    </div>
                    <p style={{ margin: '4px 0 0', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                      Wording comes from the “Arrival Reminder” template (Templates tab) — edit it there to change every reminder.
                    </p>
                  </td>
                </tr>,
              );
            }
            return rows;
          })}
        </tbody>
      </table>
      )}
      <ConfirmDialog
        isOpen={!!pendingCancel}
        title={`Cancel the arrival reminder for ${pendingCancel?.name ?? 'this job'}?`}
        message="The customer won’t get the automatic “on the way” message for this job. You can undo this straight after."
        tone="warning"
        confirmLabel="Cancel reminder"
        onConfirm={() => pendingCancel && cancelMut.mutate(pendingCancel)}
        onCancel={() => setPendingCancel(null)}
      />
    </div>
  );
}

/** Upcoming pre-wash reminders — softwash jobs that will get the Pre-Wash email
 *  ~48h before they start. Read-only preview (not queued messages) with the
 *  ability to cancel a specific one and expand the exact email that will be sent. */
function PreWashReminderPreview() {
  const qc = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const [pendingCancel, setPendingCancel] = useState<{ eventId: string; name: string } | null>(null);
  const [undo, setUndo] = useState<{ eventId: string; name: string } | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ['prewash-reminders-preview'],
    queryFn: () => api.getPrewashRemindersPreview(336),
    refetchInterval: 60_000,
  });
  const cancelMut = useMutation({
    mutationFn: (v: { eventId: string; name: string }) => api.cancelPrewashReminder(v.eventId).then(() => v),
    onSuccess: (v) => {
      setPendingCancel(null);
      setUndo(v);
      qc.invalidateQueries({ queryKey: ['prewash-reminders-preview'] });
    },
  });
  const restoreMut = useMutation({
    mutationFn: (eventId: string) => api.restorePrewashReminder(eventId),
    onSuccess: () => {
      setUndo(null);
      qc.invalidateQueries({ queryKey: ['prewash-reminders-preview'] });
    },
  });

  if (isLoading || !data) return null;
  const jobs = (data.results ?? []).filter((r) => new Date(r.start).getTime() > Date.now());
  if (jobs.length === 0 && !undo) return null;

  const fmt = (iso: string) =>
    new Date(iso).toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', timeZone: 'Australia/Brisbane' });
  const sendTime = (iso: string) => fmt(new Date(new Date(iso).getTime() - 48 * 3600000).toISOString());
  const cleanTitle = (t: string | null) =>
    (t ?? '').replace(/\b(quote|invoice)\s+(qu|inv)-?\d+/gi, '').replace(/^[\s-–]+/, '').replace(/\s{2,}/g, ' ').trim() || 'Job';

  return (
    <div style={{ ...pageCardStyle, marginBottom: 'var(--spacing-3)', borderLeft: '3px solid var(--color-accent)' }}>
      <h3 style={{ margin: 0, fontSize: 'var(--font-size-md)', fontWeight: 600 }}>🧼 Pre-wash reminders — softwash jobs</h3>
      <p style={{ margin: '4px 0 var(--spacing-2)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
        Emailed automatically ~48h before each softwash job (using the “Pre-Wash Reminder” template). Not queued messages, so they don’t appear in the list below. Click a row to see the exact email.
      </p>
      {undo && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--spacing-2)', background: 'var(--color-surface-elevated)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 'var(--spacing-2) var(--spacing-3)', marginBottom: 'var(--spacing-2)' }}>
          <span style={{ fontSize: 'var(--font-size-sm)' }}>Cancelled the pre-wash reminder for <strong>{undo.name}</strong> — it won’t be sent.</span>
          <button style={{ ...smallButtonStyle }} disabled={restoreMut.isPending} onClick={() => restoreMut.mutate(undo.eventId)}>Undo</button>
        </div>
      )}
      {jobs.length > 0 && (
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
            {['Job', 'Job starts', 'Emails (~48h before)', 'Channel', 'Goes to', '', ''].map((h, i) => (
              <th key={i} style={{ textAlign: 'left', padding: '4px 8px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {jobs.flatMap((r) => {
            const hasEmail = r.channel.startsWith('email');
            const isOpen = openId === r.eventId;
            const rows = [
              <tr key={r.eventId} style={{ borderBottom: isOpen ? 'none' : '1px solid var(--color-border)', cursor: 'pointer' }} onClick={() => setOpenId(isOpen ? null : r.eventId)}>
                <td style={{ padding: '6px 8px' }}>{cleanTitle(r.title)}</td>
                <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{fmt(r.start)}</td>
                <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', fontWeight: 600 }}>{sendTime(r.start)}</td>
                <td style={{ padding: '6px 8px' }}>
                  {hasEmail ? <span style={badgeStyle('green')}>Email</span> : <span style={{ color: 'var(--color-error)', fontWeight: 600 }}>No email ⚠</span>}
                </td>
                <td style={{ padding: '6px 8px', color: 'var(--color-text-muted)' }}>{r.contact || '—'}</td>
                <td style={{ padding: '6px 8px' }}>
                  <button
                    style={{ ...smallButtonStyle, color: 'var(--color-error)', borderColor: 'var(--color-error)' }}
                    onClick={(e) => { e.stopPropagation(); setPendingCancel({ eventId: r.eventId, name: cleanTitle(r.title) }); }}
                  >
                    Cancel
                  </button>
                </td>
                <td style={{ padding: '6px 8px', color: 'var(--color-text-muted)' }}>{isOpen ? '▾' : '▸'}</td>
              </tr>,
            ];
            if (isOpen) {
              rows.push(
                <tr key={`${r.eventId}-msg`} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td colSpan={7} style={{ padding: '0 8px 10px' }}>
                    <div style={{ background: 'var(--color-surface-elevated)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 'var(--spacing-2) var(--spacing-3)', whiteSpace: 'pre-wrap', fontSize: 'var(--font-size-sm)' }}>
                      {r.body || <span style={{ color: 'var(--color-text-muted)' }}>No email address on this job, so no message will be sent.</span>}
                    </div>
                    <p style={{ margin: '4px 0 0', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                      Wording comes from the “Pre-Wash Reminder” template (Templates tab) — edit it there to change every reminder.
                    </p>
                  </td>
                </tr>,
              );
            }
            return rows;
          })}
        </tbody>
      </table>
      )}
      <ConfirmDialog
        isOpen={!!pendingCancel}
        title={`Cancel the pre-wash reminder for ${pendingCancel?.name ?? 'this job'}?`}
        message="The customer won’t get the automatic pre-wash email for this job. You can undo this straight after."
        tone="warning"
        confirmLabel="Cancel reminder"
        onConfirm={() => pendingCancel && cancelMut.mutate(pendingCancel)}
        onCancel={() => setPendingCancel(null)}
      />
    </div>
  );
}

function BookingReminderPreview() {
  const [expandedBatch, setExpandedBatch] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['booking-reminder-preview'],
    queryFn: () => api.getBookingReminderPreview(),
    refetchInterval: 60_000,
  });

  if (isLoading) return null;
  if (!data || data.templateId == null) return null;

  function downloadCsv(items: (typeof data.batches)[0]['items'], label: string) {
    const header = 'Customer,Mobile,Contractor,Appointment,Message Preview';
    const rows = items.map((item) => {
      const esc = (s: string) => `"${(s || '').replace(/"/g, '""')}"`;
      return [
        esc(item.customerName),
        esc(item.mobile),
        esc(item.contractorName),
        esc(item.appointmentDate),
        esc(item.renderedBody),
      ].join(',');
    });
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `booking-reminder-${label}-${new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Brisbane' })}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <>
      {data.batches.map((batch, idx) => {
        const runTime = new Date(batch.runAt).toLocaleString('en-AU', {
          dateStyle: 'full',
          timeStyle: 'short',
          timeZone: 'Australia/Brisbane',
        });
        const label = `Booking Reminder — ${batch.label || (idx === 0 ? 'Tomorrow' : 'Day After')}`;
        const isExpanded = expandedBatch === idx;

        return (
          <div
            key={idx}
            style={{
              ...pageCardStyle,
              marginBottom: 'var(--spacing-3)',
              borderLeft: '3px solid var(--color-success)',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 'var(--spacing-2)',
              }}
            >
              <div>
                <h3
                  style={{
                    margin: 0,
                    fontSize: 'var(--font-size-md)',
                    fontWeight: 600,
                    color: 'var(--color-text)',
                  }}
                >
                  {label}
                </h3>
                <p
                  style={{
                    margin: '4px 0 0 0',
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--color-text-muted)',
                  }}
                >
                  Scheduled for <strong>{runTime}</strong> · {batch.batchSize} message
                  {batch.batchSize === 1 ? '' : 's'} ·{' '}
                  <span style={{ fontFamily: 'monospace', fontSize: '11px' }}>
                    template: {data.templateName}
                  </span>
                </p>
              </div>
              <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                <button
                  style={smallButtonStyle}
                  onClick={() => downloadCsv(batch.items, idx === 0 ? 'next' : 'following')}
                  disabled={batch.batchSize === 0}
                >
                  ↓ Download CSV
                </button>
                <button
                  style={smallButtonStyle}
                  onClick={() => setExpandedBatch(isExpanded ? null : idx)}
                  disabled={batch.batchSize === 0}
                >
                  {isExpanded
                    ? '▲ Hide messages'
                    : `▼ Show ${batch.batchSize} message${batch.batchSize === 1 ? '' : 's'}`}
                </button>
              </div>
            </div>

            {batch.batchSize === 0 && (
              <p
                style={{
                  margin: 0,
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-text-muted)',
                  fontStyle: 'italic',
                }}
              >
                No bookings eligible. Criteria: appointment in 24–48h, booked 3+ days in advance,
                SMS not opted out, reminder not yet sent.
              </p>
            )}

            {isExpanded && batch.items.length > 0 && (
              <div
                style={{
                  marginTop: 'var(--spacing-2)',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  border: '1px solid var(--color-border)',
                  borderRadius: '4px',
                }}
              >
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: 'var(--font-size-sm)',
                  }}
                >
                  <thead
                    style={{
                      position: 'sticky',
                      top: 0,
                      background: 'var(--color-surface)',
                      zIndex: 1,
                    }}
                  >
                    <tr>
                      <th style={headerCellStyle}>Customer</th>
                      <th style={headerCellStyle}>Mobile</th>
                      <th style={headerCellStyle}>Contractor</th>
                      <th style={headerCellStyle}>Appointment</th>
                      <th style={headerCellStyle}>Message Preview</th>
                    </tr>
                  </thead>
                  <tbody>
                    {batch.items.map((item) => (
                      <tr
                        key={item.bookingId}
                        style={{ borderTop: '1px solid var(--color-border)' }}
                      >
                        <td style={bodyCellStyle}>{item.customerName || '—'}</td>
                        <td style={{ ...bodyCellStyle, fontFamily: 'monospace' }}>{item.mobile}</td>
                        <td style={bodyCellStyle}>{item.contractorName}</td>
                        <td style={bodyCellStyle}>{item.appointmentDate}</td>
                        <td
                          style={{
                            ...bodyCellStyle,
                            fontFamily: 'monospace',
                            fontSize: '11px',
                            maxWidth: '400px',
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {item.renderedBody}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

function RebookingReminderPreview() {
  const [expanded, setExpanded] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['rebooking-preview'],
    queryFn: () => api.getRebookingPreview(500),
    refetchInterval: 60_000,
  });

  if (isLoading) return null;
  if (!data || data.templateId == null) return null;
  if (data.batchSize === 0) return null;

  const nextRun = new Date(data.nextRunAt);
  const nextRunFormatted = nextRun.toLocaleString('en-AU', {
    dateStyle: 'full',
    timeStyle: 'short',
    timeZone: 'Australia/Brisbane',
  });

  return (
    <div
      style={{
        ...pageCardStyle,
        marginBottom: 'var(--spacing-3)',
        borderLeft: '3px solid var(--color-accent)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--spacing-2)',
        }}
      >
        <div>
          <h3
            style={{
              margin: 0,
              fontSize: 'var(--font-size-md)',
              fontWeight: 600,
              color: 'var(--color-text)',
            }}
          >
            Next Rebooking Reminder Batch
          </h3>
          <p
            style={{
              margin: '4px 0 0 0',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-muted)',
            }}
          >
            Scheduled for <strong>{nextRunFormatted}</strong> · {data.batchSize} customer
            {data.batchSize === 1 ? '' : 's'} ·{' '}
            <span style={{ fontFamily: 'monospace', fontSize: '11px' }}>
              template: {data.templateName}
            </span>
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
          <button
            style={smallButtonStyle}
            onClick={() => {
              if (!data || data.batchSize === 0) return;
              const header = 'Customer,Mobile,Last Visit,Message Preview';
              const rows = data.items.map((item) => {
                const esc = (s: string) => `"${(s || '').replace(/"/g, '""')}"`;
                const lastVisit = item.lastAppointmentDate
                  ? new Date(item.lastAppointmentDate).toLocaleDateString('en-AU', {
                      dateStyle: 'medium',
                    })
                  : '';
                return [
                  esc(item.customerName),
                  esc(item.mobile),
                  esc(lastVisit),
                  esc(item.renderedBody),
                ].join(',');
              });
              const csv = [header, ...rows].join('\n');
              const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `rebooking-batch-${new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Brisbane' })}.csv`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            disabled={data.batchSize === 0}
          >
            ↓ Download CSV
          </button>
          <button
            style={smallButtonStyle}
            onClick={() => setExpanded((v) => !v)}
            disabled={data.batchSize === 0}
          >
            {expanded
              ? '▲ Hide messages'
              : `▼ Show ${data.batchSize} message${data.batchSize === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>

      {data.batchSize === 0 && (
        <p
          style={{
            margin: 0,
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-muted)',
            fontStyle: 'italic',
          }}
        >
          No customers are currently eligible. Criteria: last visit ≥ 4 weeks ago, SMS subscribed,
          not already reminded in the last 30 days.
        </p>
      )}

      {expanded && data.items.length > 0 && (
        <div
          style={{
            marginTop: 'var(--spacing-2)',
            maxHeight: '400px',
            overflowY: 'auto',
            border: '1px solid var(--color-border)',
            borderRadius: '4px',
          }}
        >
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
          >
            <thead
              style={{
                position: 'sticky',
                top: 0,
                background: 'var(--color-surface)',
                zIndex: 1,
              }}
            >
              <tr>
                <th style={headerCellStyle}>Customer</th>
                <th style={headerCellStyle}>Mobile</th>
                <th style={headerCellStyle}>Last Visit</th>
                <th style={headerCellStyle}>Message Preview</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((item) => (
                <tr key={item.customerId} style={{ borderTop: '1px solid var(--color-border)' }}>
                  <td style={bodyCellStyle}>{item.customerName || '—'}</td>
                  <td style={{ ...bodyCellStyle, fontFamily: 'monospace' }}>{item.mobile}</td>
                  <td style={bodyCellStyle}>
                    {item.lastAppointmentDate
                      ? new Date(item.lastAppointmentDate).toLocaleDateString('en-AU', {
                          dateStyle: 'medium',
                        })
                      : '—'}
                  </td>
                  <td
                    style={{
                      ...bodyCellStyle,
                      fontFamily: 'monospace',
                      fontSize: '11px',
                      maxWidth: '400px',
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {item.renderedBody}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const headerCellStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: 'var(--spacing-2)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--color-text-muted)',
  borderBottom: '1px solid var(--color-border)',
};

const bodyCellStyle: React.CSSProperties = {
  padding: 'var(--spacing-2)',
  verticalAlign: 'top',
  color: 'var(--color-text)',
};


// Panel shown at the top of the Scheduled tab: upcoming quote follow-ups (sent
// automatically 2 weeks after a quote, at the next 8am Brisbane Tue/Wed/Thu).
function QuoteFollowupSchedulePreview() {
  const qc = useQueryClient();
  const [openQuoteId, setOpenQuoteId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { data } = useQuery({ queryKey: ['quote-followup-preview'], queryFn: () => api.getQuoteFollowupPreview(), refetchInterval: 60_000 });
  const cancelMut = useMutation({
    mutationFn: (id: number) => api.cancelScheduledMessage(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['quote-followup-preview'] }); qc.invalidateQueries({ queryKey: ['scheduled-messages'] }); },
  });
  const queue = data?.queue ?? [];
  const skipped = data?.skipped ?? [];
  if (queue.length === 0 && skipped.length === 0) return null;
  // Sent follow-ups are done (see Send Log); the panel lists what's still to come.
  const sentCount = queue.filter((q) => q.status === 'sent').length;
  const shown = queue.filter((q) => q.status !== 'sent' && q.status !== 'cancelled');
  const emailN = shown.filter((q) => q.channel === 'email').length;
  const smsN = shown.filter((q) => q.channel === 'sms').length;
  const statusText = (s: string | null) => (s === 'pending' ? 'Queued' : s === 'failed' ? 'Failed' : 'Will queue');
  const statusColor = (s: string | null) => (s === 'failed' ? 'var(--color-error)' : s === 'pending' ? 'var(--color-success)' : 'var(--color-text-muted)');
  const dt = (iso: string) => {
    const d = new Date(iso);
    return isNaN(d.getTime()) ? '—' : d.toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', timeZone: 'Australia/Brisbane' });
  };
  const dateFmt = (d: string) => new Date(d).toLocaleDateString('en-AU', { timeZone: 'Australia/Brisbane', day: 'numeric', month: 'short', year: 'numeric' });
  return (
    <div style={{ ...pageCardStyle, marginBottom: 'var(--spacing-3)', borderLeft: '3px solid var(--color-accent)' }}>
      <h3 style={{ margin: 0, fontSize: 'var(--font-size-md)', fontWeight: 600 }}>📨 Quote follow-ups: 2 weeks after a quote</h3>
      <p style={{ margin: '4px 0 var(--spacing-2)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
        Sent automatically at the next 8:00am Brisbane Tue/Wed/Thu on the quote's channel, while the quote is still SENT.
        Referral-sent quotes are excluded. Click a row to see the exact message. {shown.length} upcoming ({emailN} email, {smsN} SMS){sentCount ? `; ${sentCount} already sent (use the Sent filter above)` : ''}.
      </p>
      {shown.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                {['Quote', 'Customer', 'Channel', 'Goes to', 'Sends', 'Status', '', ''].map((h, i) => (
                  <th key={i} style={{ textAlign: 'left', padding: '4px 8px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.flatMap((q) => {
                const open = expandedId === q.quoteId;
                const out = [
                  <tr key={q.quoteId} onClick={() => setExpandedId(open ? null : q.quoteId)} style={{ borderBottom: '1px solid var(--color-border)', cursor: 'pointer' }}>
                    <td style={{ padding: '4px 8px', fontWeight: 600 }}>
                      <button onClick={(e) => { e.stopPropagation(); setOpenQuoteId(q.quoteId); }} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-teal, #00B097)', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>{q.quoteNumber}</button>
                    </td>
                    <td style={{ padding: '4px 8px' }}>{q.contactName}</td>
                    <td style={{ padding: '4px 8px', textTransform: 'uppercase' }}>{q.channel}</td>
                    <td style={{ padding: '4px 8px', color: 'var(--color-text-muted)' }}>{q.recipient}</td>
                    <td style={{ padding: '4px 8px', color: 'var(--color-text-muted)', whiteSpace: 'nowrap', fontWeight: 600 }}>{dt(q.sendAt)}</td>
                    <td style={{ padding: '4px 8px', color: statusColor(q.status) }}>{statusText(q.status)}</td>
                    <td style={{ padding: '4px 8px' }}>
                      {q.status === 'pending' && q.scheduleId != null && (
                        <button
                          style={{ ...smallButtonStyle, color: 'var(--color-error)', borderColor: 'var(--color-error)' }}
                          disabled={cancelMut.isPending}
                          title="Cancel this follow-up (it won't be sent)"
                          onClick={(e) => { e.stopPropagation(); cancelMut.mutate(q.scheduleId as number); }}
                        >
                          Cancel
                        </button>
                      )}
                    </td>
                    <td style={{ padding: '4px 8px', color: 'var(--color-text-muted)' }}>{open ? '▾' : '▸'}</td>
                  </tr>,
                ];
                if (open) {
                  out.push(
                    <tr key={`${q.quoteId}-msg`} style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td colSpan={8} style={{ padding: '0 8px 10px' }}>
                        <div style={{ background: 'var(--color-surface-elevated)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 'var(--spacing-2) var(--spacing-3)', whiteSpace: 'pre-wrap', fontSize: 'var(--font-size-sm)' }}>
                          {q.channel === 'email' && q.subject && (
                            <div style={{ fontWeight: 600, marginBottom: 6 }}>Subject: {q.subject}</div>
                          )}
                          {q.body}
                        </div>
                        <p style={{ margin: '4px 0 0', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                          Why {q.channel}: {q.reason}. Quote sent {dateFmt(q.docDate)}. Wording comes from the “Quote follow-up” templates (Templates tab).
                        </p>
                      </td>
                    </tr>,
                  );
                }
                return out;
              })}
            </tbody>
          </table>
        </div>
      )}
      {skipped.length > 0 && (
        <div style={{ marginTop: 'var(--spacing-2)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
          {skipped.length} skipped (no valid contact): {skipped.map((s) => s.quoteNumber).join(', ')}
        </div>
      )}
      {openQuoteId && (
        <QuotePortalModal
          key={openQuoteId}
          quoteId={openQuoteId}
          onClose={() => { setOpenQuoteId(null); qc.invalidateQueries({ queryKey: ['quote-followup-preview'] }); }}
          onChanged={() => qc.invalidateQueries({ queryKey: ['quote-followup-preview'] })}
        />
      )}
    </div>
  );
}

// Upcoming evergreen auto win-back sends. These aren't queued rows (the campaign
// sends live on its Tue/Wed/Thu 8am run), so we surface them here for a single
// "what's going out" view, alongside the other automatic-reminder previews.
function AutoWinbackSchedulePreview() {
  const qc = useQueryClient();
  const [openId, setOpenId] = useState<number | null>(null);
  const [pendingCancel, setPendingCancel] = useState<{ customerId: number; name: string } | null>(null);
  const [undo, setUndo] = useState<{ customerId: number; name: string } | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ['auto-winback-preview'],
    queryFn: () => api.getAutoWinbackPreview(),
    refetchInterval: 60_000,
  });
  const cancelMut = useMutation({
    mutationFn: (v: { customerId: number; name: string }) => api.cancelAutoWinback(v.customerId).then(() => v),
    onSuccess: (v) => {
      setPendingCancel(null);
      setUndo(v);
      qc.invalidateQueries({ queryKey: ['auto-winback-preview'] });
    },
  });
  const restoreMut = useMutation({
    mutationFn: (customerId: number) => api.restoreAutoWinback(customerId),
    onSuccess: () => {
      setUndo(null);
      qc.invalidateQueries({ queryKey: ['auto-winback-preview'] });
    },
  });

  if (isLoading || !data) return null;
  const candidates = data.candidates ?? [];
  if (candidates.length === 0 && !undo) return null;

  // Next automatic run: the next Tue/Wed/Thu at 8:00am Brisbane.
  const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const MO = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const bneNow = new Date(new Date().toLocaleString('en-US', { timeZone: 'Australia/Brisbane' }));
  const nextRun = (() => {
    for (let i = 0; i < 8; i++) {
      const d = new Date(bneNow);
      d.setDate(bneNow.getDate() + i);
      if ([2, 3, 4].includes(d.getDay())) {
        if (i === 0 && bneNow.getHours() >= 8) continue; // today, already past 8am
        d.setHours(8, 0, 0, 0);
        return d;
      }
    }
    return bneNow;
  })();
  const runLabel = `${WD[nextRun.getDay()]} ${nextRun.getDate()} ${MO[nextRun.getMonth()]}, 8:00am`;

  return (
    <div style={{ ...pageCardStyle, marginBottom: 'var(--spacing-3)', borderLeft: '3px solid var(--color-gold, #FEC553)' }}>
      <h3 style={{ margin: 0, fontSize: 'var(--font-size-md)', fontWeight: 600 }}>🔁 Auto win-back (dormant customers)</h3>
      <p style={{ margin: '4px 0 var(--spacing-2)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
        Sent automatically on the next Tue/Wed/Thu 8:00am run (SMS where we have a mobile, otherwise email). These aren’t queued messages, so they don’t appear in the list below. Deduped, opt-outs skipped, up to 30/day. Click a row to see the exact message. Wording lives in the “Win-back (dormant customers)” templates under <strong>Templates → Campaign templates</strong>.
      </p>
      {undo && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--spacing-2)', background: 'var(--color-surface-elevated)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 'var(--spacing-2) var(--spacing-3)', marginBottom: 'var(--spacing-2)' }}>
          <span style={{ fontSize: 'var(--font-size-sm)' }}>Cancelled the win-back for <strong>{undo.name}</strong> — they won’t be emailed or texted.</span>
          <button style={{ ...smallButtonStyle }} disabled={restoreMut.isPending} onClick={() => restoreMut.mutate(undo.customerId)}>Undo</button>
        </div>
      )}
      {candidates.length > 0 && (
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
            {['Customer', 'Sends', 'Channel', 'Goes to', '', ''].map((h, i) => (
              <th key={i} style={{ textAlign: 'left', padding: '4px 8px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {candidates.flatMap((c) => {
            const isOpen = openId === c.customerId;
            const chan = c.channel === 'sms' ? 'SMS' : 'Email';
            const goesTo = c.channel === 'sms' ? c.mobile : c.email;
            const rows = [
              <tr key={c.customerId} style={{ borderBottom: isOpen ? 'none' : '1px solid var(--color-border)', cursor: 'pointer' }} onClick={() => setOpenId(isOpen ? null : c.customerId)}>
                <td style={{ padding: '6px 8px' }}>{c.name || c.email || c.mobile}</td>
                <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', fontWeight: 600 }}>{runLabel}</td>
                <td style={{ padding: '6px 8px' }}><span style={badgeStyle(c.channel === 'sms' ? 'gold' : 'green')}>{chan}</span></td>
                <td style={{ padding: '6px 8px', color: 'var(--color-text-muted)' }}>{goesTo || '—'}</td>
                <td style={{ padding: '6px 8px' }}>
                  <button
                    style={{ ...smallButtonStyle, color: 'var(--color-error)', borderColor: 'var(--color-error)' }}
                    onClick={(e) => { e.stopPropagation(); setPendingCancel({ customerId: c.customerId, name: c.name || c.email || 'this customer' }); }}
                  >
                    Cancel
                  </button>
                </td>
                <td style={{ padding: '6px 8px', color: 'var(--color-text-muted)' }}>{isOpen ? '▾' : '▸'}</td>
              </tr>,
            ];
            if (isOpen) {
              rows.push(
                <tr key={`${c.customerId}-msg`} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td colSpan={6} style={{ padding: '0 8px 10px' }}>
                    <div style={{ background: 'var(--color-surface-elevated)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 'var(--spacing-2) var(--spacing-3)', whiteSpace: 'pre-wrap', fontSize: 'var(--font-size-sm)' }}>
                      {c.message || <span style={{ color: 'var(--color-text-muted)' }}>No message.</span>}
                    </div>
                    <p style={{ margin: '4px 0 0', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                      Wording comes from the “Win-back (dormant customers){c.channel === 'sms' ? ' SMS' : ''}” template (Campaigns → Templates) — edit it there to change every win-back.
                    </p>
                  </td>
                </tr>,
              );
            }
            return rows;
          })}
        </tbody>
      </table>
      )}
      <ConfirmDialog
        isOpen={!!pendingCancel}
        title={`Cancel the win-back for ${pendingCancel?.name ?? 'this customer'}?`}
        message="They won’t be included in the automatic win-back. You can undo this straight after."
        tone="warning"
        confirmLabel="Cancel win-back"
        onConfirm={() => pendingCancel && cancelMut.mutate(pendingCancel)}
        onCancel={() => setPendingCancel(null)}
      />
    </div>
  );
}

// Post-job review + referral engine widget. Mirrors QuoteFollowupSchedulePreview:
// upcoming / sent (with click counts) / skipped-with-reasons, plus the referral
// ledger with status controls.
function ReviewReferralWidget() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['review-referral-preview'], queryFn: () => api.getReviewReferralPreview(), refetchInterval: 60_000 });
  const { data: ledger } = useQuery({ queryKey: ['referrals'], queryFn: () => api.listReferrals() });
  const [creditFor, setCreditFor] = useState<number | null>(null);
  const [creditVal, setCreditVal] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ referrerName: '', referredName: '', referredContact: '' });
  const advance = useMutation({
    mutationFn: (v: { id: number; status: string; referredJobValue?: number }) => api.advanceReferral(v.id, v),
    onSuccess: () => { setCreditFor(null); setCreditVal(''); qc.invalidateQueries({ queryKey: ['referrals'] }); },
  });
  const addRef = useMutation({
    mutationFn: () => api.createReferral(addForm),
    onSuccess: () => { setShowAdd(false); setAddForm({ referrerName: '', referredName: '', referredContact: '' }); qc.invalidateQueries({ queryKey: ['referrals'] }); },
  });
  // Cancel / preview an upcoming touch, mirroring the arrival-reminder widget.
  const [openKey, setOpenKey] = useState<string | null>(null);
  const [pendingCancel, setPendingCancel] = useState<{ eventId: string; touch: 'review' | 'referral'; name: string } | null>(null);
  const [undo, setUndo] = useState<{ eventId: string; touch: 'review' | 'referral'; name: string } | null>(null);
  const cancelMut = useMutation({
    mutationFn: (v: { eventId: string; touch: 'review' | 'referral'; name: string }) => api.cancelReviewReferral(v.eventId, v.touch).then(() => v),
    onSuccess: (v) => { setPendingCancel(null); setUndo(v); qc.invalidateQueries({ queryKey: ['review-referral-preview'] }); },
  });
  const restoreMut = useMutation({
    mutationFn: (v: { eventId: string; touch: 'review' | 'referral' }) => api.restoreReviewReferral(v.eventId, v.touch),
    onSuccess: () => { setUndo(null); qc.invalidateQueries({ queryKey: ['review-referral-preview'] }); },
  });
  if (isLoading || !data) return null;
  const rows = data.rows || [];
  const upcoming = rows.filter((r) => r.status === 'upcoming');
  const sent = rows.filter((r) => r.status === 'sent');
  const skipped = rows.filter((r) => r.status === 'skipped');
  const failed = rows.filter((r) => r.status === 'failed');
  const skipCounts: Record<string, number> = {};
  for (const r of skipped) if (r.reason) skipCounts[r.reason] = (skipCounts[r.reason] || 0) + 1;
  const nextStatus: Record<string, string | null> = { mentioned: 'quoted', quoted: 'completed', completed: 'credited', credited: null };
  const fmtDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'Australia/Brisbane' }) : '');
  const fmtWhen = (iso?: string | null) => (iso ? new Date(iso).toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', timeZone: 'Australia/Brisbane' }) : '');
  const referrals = ledger || [];
  // Collapse identical sends (same customer, touch, channel, day) into one row
  // with a count: a multi-day job used to send the same ask once per calendar
  // event, and those historical duplicates would otherwise read as a live bug.
  const sentGroups = (() => {
    const map = new Map<string, { row: (typeof sent)[number]; count: number; clicks: number }>();
    for (const r of sent) {
      const key = `${r.firstName}|${r.touch}|${r.channel}|${fmtDate(r.sentAt)}`;
      const g = map.get(key);
      if (g) { g.count++; g.clicks += r.clicks ?? 0; } else map.set(key, { row: r, count: 1, clicks: r.clicks ?? 0 });
    }
    return [...map.values()];
  })();

  return (
    <div style={{ ...pageCardStyle, marginBottom: 'var(--spacing-3)', borderLeft: '3px solid var(--color-gold, #FEC553)' }}>
      <h3 style={{ margin: 0, fontSize: 'var(--font-size-md)', fontWeight: 600 }}>⭐ Review + referral engine</h3>
      <p style={{ margin: '4px 0 var(--spacing-2)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
        Review ask ~1 day after a thumbs-up completion, referral ask ~7 days later. Sends Tue/Wed/Thu between 8am and 6pm, one of each per customer, opt-outs skipped, 30/day.
      </p>
      {data.notConfigured && (
        <p style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
          ⚠ Google review URL not configured — review asks are on hold. Set it in the review + referral settings.
        </p>
      )}

      {/* Undo banner (after a cancel) */}
      {undo && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--spacing-2)', background: 'var(--color-surface-elevated)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 'var(--spacing-2) var(--spacing-3)', margin: '8px 0' }}>
          <span style={{ fontSize: 'var(--font-size-sm)' }}>Cancelled the {undo.touch} for <strong>{undo.name}</strong> — it won’t be sent.</span>
          <button style={{ ...smallButtonStyle }} disabled={restoreMut.isPending} onClick={() => restoreMut.mutate({ eventId: undo.eventId, touch: undo.touch })}>Undo</button>
        </div>
      )}

      {/* Upcoming */}
      <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)', margin: '8px 0 4px' }}>Upcoming ({upcoming.length})</div>
      {upcoming.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)', margin: 0 }}>Nothing queued.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}>
          <thead><tr style={{ borderBottom: '1px solid var(--color-border)' }}>{['Customer', 'Touch', 'Sends', 'Channel', 'Goes to', '', ''].map((h, i) => (<th key={i} style={{ textAlign: 'left', padding: '4px 8px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>{h}</th>))}</tr></thead>
          <tbody>{upcoming.flatMap((r) => {
            const key = `${r.eventId}:${r.touch}`;
            const isOpen = openKey === key;
            const out = [
              <tr key={key} style={{ borderBottom: isOpen ? 'none' : '1px solid var(--color-border)', cursor: 'pointer' }} onClick={() => setOpenKey(isOpen ? null : key)}>
                <td style={{ padding: '6px 8px' }}>{r.firstName}</td>
                <td style={{ padding: '6px 8px' }}>{r.touch}</td>
                <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', fontWeight: 600 }}>{fmtWhen(r.sendAt)}</td>
                <td style={{ padding: '6px 8px' }}><span style={badgeStyle(r.channel === 'sms' ? 'gold' : 'green')}>{r.channel === 'sms' ? 'SMS' : 'Email'}</span></td>
                <td style={{ padding: '6px 8px', color: 'var(--color-text-muted)' }}>{r.goesTo || '—'}</td>
                <td style={{ padding: '6px 8px' }}>
                  <button style={{ ...smallButtonStyle, color: 'var(--color-error)', borderColor: 'var(--color-error)' }} onClick={(e) => { e.stopPropagation(); setPendingCancel({ eventId: r.eventId, touch: r.touch, name: r.firstName }); }}>Cancel</button>
                </td>
                <td style={{ padding: '6px 8px', color: 'var(--color-text-muted)' }}>{isOpen ? '▾' : '▸'}</td>
              </tr>,
            ];
            if (isOpen) {
              out.push(
                <tr key={`${key}-msg`} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td colSpan={7} style={{ padding: '0 8px 10px' }}>
                    <div style={{ background: 'var(--color-surface-elevated)', border: '1px solid var(--color-border)', borderRadius: 8, padding: 'var(--spacing-2) var(--spacing-3)', whiteSpace: 'pre-wrap', fontSize: 'var(--font-size-sm)' }}>
                      {r.subject && <div style={{ fontWeight: 600, marginBottom: 4 }}>{r.subject}</div>}
                      {r.message || <span style={{ color: 'var(--color-text-muted)' }}>No message.</span>}
                    </div>
                    <p style={{ margin: '4px 0 0', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                      Wording comes from the “{r.touch === 'review' ? 'Review' : 'Referral'} ask” template (Templates → Campaign templates).
                    </p>
                  </td>
                </tr>,
              );
            }
            return out;
          })}</tbody>
        </table>
      )}

      {/* Failed: a send the provider rejected. Kept on record so the engine
          does not retry by itself; Retry clears it once the contact is fixed. */}
      {failed.length > 0 && (
        <>
          <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)', margin: '10px 0 4px', color: 'var(--color-error)' }}>Failed ({failed.length})</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}>
            <thead><tr style={{ borderBottom: '1px solid var(--color-border)' }}>{['Customer', 'Touch', 'Channel', 'Goes to', 'Attempted', 'Why', ''].map((h, i) => (<th key={i} style={{ textAlign: 'left', padding: '4px 8px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>{h}</th>))}</tr></thead>
            <tbody>{failed.map((r) => (
              <tr key={`${r.eventId}:${r.touch}`} style={{ borderBottom: '1px solid var(--color-border)' }}>
                <td style={{ padding: '6px 8px' }}>{r.firstName}</td>
                <td style={{ padding: '6px 8px' }}>{r.touch}</td>
                <td style={{ padding: '6px 8px' }}>{r.channel === 'sms' ? 'SMS' : 'Email'}</td>
                <td style={{ padding: '6px 8px', color: 'var(--color-text-muted)' }}>{r.goesTo || '—'}</td>
                <td style={{ padding: '6px 8px', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>{fmtDate(r.sentAt)}</td>
                <td style={{ padding: '6px 8px', color: 'var(--color-error)' }}>{r.reason}</td>
                <td style={{ padding: '6px 8px' }}>
                  <button style={{ ...smallButtonStyle }} disabled={restoreMut.isPending} title="Fix the customer's contact first, then retry on the next Tue/Wed/Thu 8am run" onClick={() => restoreMut.mutate({ eventId: r.eventId, touch: r.touch })}>Retry</button>
                </td>
              </tr>
            ))}</tbody>
          </table>
        </>
      )}

      {/* Sent (with click counts) */}
      {sent.length > 0 && (
        <>
          <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)', margin: '10px 0 4px' }}>Sent ({sent.length})</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}>
            <thead><tr style={{ borderBottom: '1px solid var(--color-border)' }}>{['Customer', 'Touch', 'Channel', 'Sent', 'Clicks'].map((h, i) => (<th key={i} style={{ textAlign: 'left', padding: '4px 8px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>{h}</th>))}</tr></thead>
            <tbody>{sentGroups.map(({ row: r, count, clicks }, i) => (
              <tr key={i} style={{ borderBottom: '1px solid var(--color-border)' }}>
                <td style={{ padding: '6px 8px' }}>{r.firstName}</td>
                <td style={{ padding: '6px 8px' }}>{r.touch}</td>
                <td style={{ padding: '6px 8px' }}>{r.channel === 'sms' ? 'SMS' : 'Email'}</td>
                <td style={{ padding: '6px 8px', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>
                  {fmtDate(r.sentAt)}
                  {count > 1 && <span title={`Sent ${count} times that day: one per calendar event of a multi-day job, before de-duplication was added`} style={{ marginLeft: 6, color: 'var(--color-error)', fontWeight: 600 }}>×{count}</span>}
                </td>
                <td style={{ padding: '6px 8px', fontWeight: 600 }}>{clicks}</td>
              </tr>
            ))}</tbody>
          </table>
        </>
      )}

      {/* Skipped with reasons */}
      {Object.keys(skipCounts).length > 0 && (
        <p style={{ margin: '10px 0 0', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
          Skipped: {Object.entries(skipCounts).map(([reason, n]) => `${reason} (${n})`).join(' · ')}
        </p>
      )}

      {/* Referral ledger */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '14px 0 4px' }}>
        <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>Referral ledger ({referrals.length})</div>
        <button style={smallButtonStyle} onClick={() => setShowAdd((v) => !v)}>{showAdd ? '✕ Cancel' : '＋ Add referral'}</button>
      </div>
      {showAdd && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
          <input placeholder="Referrer name" value={addForm.referrerName} onChange={(e) => setAddForm({ ...addForm, referrerName: e.target.value })} style={{ flex: 1, minWidth: 120, padding: 6, border: '1px solid var(--color-border)', borderRadius: 6 }} />
          <input placeholder="Referred name" value={addForm.referredName} onChange={(e) => setAddForm({ ...addForm, referredName: e.target.value })} style={{ flex: 1, minWidth: 120, padding: 6, border: '1px solid var(--color-border)', borderRadius: 6 }} />
          <input placeholder="Referred contact" value={addForm.referredContact} onChange={(e) => setAddForm({ ...addForm, referredContact: e.target.value })} style={{ flex: 1, minWidth: 120, padding: 6, border: '1px solid var(--color-border)', borderRadius: 6 }} />
          <button style={{ ...smallButtonStyle }} disabled={addRef.isPending || !addForm.referredName} onClick={() => addRef.mutate()}>Save</button>
        </div>
      )}
      {referrals.length === 0 ? (
        <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)', margin: 0 }}>No referrals logged.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}>
          <thead><tr style={{ borderBottom: '1px solid var(--color-border)' }}>{['Referrer', 'Referred', 'Contact', 'Status', 'Value', ''].map((h, i) => (<th key={i} style={{ textAlign: 'left', padding: '4px 8px', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', textTransform: 'uppercase' }}>{h}</th>))}</tr></thead>
          <tbody>{referrals.map((r) => {
            const next = nextStatus[r.status];
            return (
              <tr key={r.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                <td style={{ padding: '6px 8px' }}>{r.referrerName || '—'}</td>
                <td style={{ padding: '6px 8px' }}>{r.referredName || '—'}</td>
                <td style={{ padding: '6px 8px', color: 'var(--color-text-muted)' }}>{r.referredContact || '—'}</td>
                <td style={{ padding: '6px 8px' }}><span style={badgeStyle(r.status === 'credited' ? 'green' : 'gold')}>{r.status}</span></td>
                <td style={{ padding: '6px 8px' }}>{r.referredJobValue != null ? `$${r.referredJobValue}` : '—'}</td>
                <td style={{ padding: '6px 8px' }}>
                  {advance.isError && creditFor === r.id && <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-xs)', marginRight: 6 }}>{(advance.error as Error).message}</span>}
                  {next === 'credited' ? (
                    creditFor === r.id ? (
                      <span style={{ display: 'inline-flex', gap: 4 }}>
                        <input placeholder="job $" value={creditVal} onChange={(e) => setCreditVal(e.target.value)} style={{ width: 70, padding: 4, border: '1px solid var(--color-border)', borderRadius: 6 }} />
                        <button style={smallButtonStyle} disabled={advance.isPending} onClick={() => advance.mutate({ id: r.id, status: 'credited', referredJobValue: Number(creditVal) })}>Credit</button>
                      </span>
                    ) : (
                      <button style={smallButtonStyle} onClick={() => { setCreditFor(r.id); setCreditVal(r.referredJobValue != null ? String(r.referredJobValue) : ''); }}>→ credited</button>
                    )
                  ) : next ? (
                    <button style={smallButtonStyle} disabled={advance.isPending} onClick={() => advance.mutate({ id: r.id, status: next })}>→ {next}</button>
                  ) : null}
                </td>
              </tr>
            );
          })}</tbody>
        </table>
      )}
      <ConfirmDialog
        isOpen={!!pendingCancel}
        title={`Cancel the ${pendingCancel?.touch ?? ''} for ${pendingCancel?.name ?? 'this customer'}?`}
        message="This message won’t be sent. You can undo it straight after."
        tone="warning"
        confirmLabel="Cancel it"
        onConfirm={() => pendingCancel && cancelMut.mutate(pendingCancel)}
        onCancel={() => setPendingCancel(null)}
      />
    </div>
  );
}

function ScheduledTab() {
  const qc = useQueryClient();
  // Default to upcoming only — the sent history lives in the Send Log, so showing
  // sent rows here just duplicated it. Staff can still switch the filter to see them.
  const [statusFilter, setStatusFilter] = useState('pending');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['scheduled-messages', statusFilter, page],
    queryFn: () =>
      api.getScheduledMessages({
        ...(statusFilter ? { status: statusFilter } : {}),
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      }),
    refetchInterval: 30_000,
  });

  const schedules: any[] = (data as any)?.schedules || [];
  const total: number = (data as any)?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const cancelMutation = useMutation({
    mutationFn: (id: number) => api.cancelScheduledMessage(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scheduled-messages'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.deleteScheduledMessage(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scheduled-messages'] }),
  });

  function statusBadgeColor(status: string): string {
    if (status === 'pending') return 'gold';
    if (status === 'sent') return 'green';
    if (status === 'failed') return 'red';
    return 'grey';
  }

  const STATUS_FILTERS = ['', 'pending', 'sent', 'failed', 'cancelled'] as const;

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 'var(--spacing-3)',
        }}
      >
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center' }}>
          <span
            style={{
              fontSize: 'var(--font-size-xs)',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              color: 'var(--color-text-muted)',
            }}
          >
            Status:
          </span>
          {STATUS_FILTERS.map((s) => (
            <button
              key={s || 'all'}
              style={{
                ...smallButtonStyle,
                ...(statusFilter === s
                  ? {
                      backgroundColor: 'var(--color-accent)',
                      color: 'var(--color-text-inverse)',
                      borderColor: 'var(--color-accent)',
                    }
                  : {}),
              }}
              onClick={() => { setStatusFilter(s); setPage(0); }}
            >
              {s || 'All'}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
          <button style={smallButtonStyle} onClick={() => refetch()}>
            ↻ Refresh
          </button>
          <button style={primaryButtonStyle} onClick={() => setShowForm((v) => !v)}>
            {showForm ? '✕ Cancel' : '+ Schedule Message'}
          </button>
        </div>
      </div>

      {showForm && <ScheduleForm onCreated={() => setShowForm(false)} />}

      {/* These are previews of upcoming (not-yet-sent) automatic reminders, so
          only show them under All / pending — not sent/failed/cancelled. */}
      {(statusFilter === '' || statusFilter === 'pending') && (
        <>
          <QuoteFollowupSchedulePreview />
          <ReviewReferralWidget />
          <AutoWinbackSchedulePreview />
          <PreWashReminderPreview />
          <ArrivalReminderPreview />
          <BookingReminderPreview />
          <RebookingReminderPreview />
        </>
      )}

      <div style={pageCardStyle}>
        {isLoading ? (
          <p
            style={{
              color: 'var(--color-text-muted)',
              textAlign: 'center',
              padding: 'var(--spacing-4)',
            }}
          >
            Loading...
          </p>
        ) : schedules.length === 0 ? (
          <p
            style={{
              color: 'var(--color-text-muted)',
              textAlign: 'center',
              padding: 'var(--spacing-4)',
            }}
          >
            No scheduled messages{statusFilter ? ` with status "${statusFilter}"` : ''}.
          </p>
        ) : (
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
          >
            <thead>
              <tr style={{ borderBottom: '2px solid var(--color-border)' }}>
                {[
                  'Scheduled For',
                  'Template',
                  'Channel',
                  'Contact',
                  'Recipient',
                  'Status',
                  'Sent At',
                  'Scheduled By',
                  'Actions',
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      textAlign: 'left',
                      fontWeight: 600,
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-muted)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {schedules.flatMap((row: any) => {
                const dataRow = (
                  <tr
                    key={row.id}
                    style={{
                      borderBottom: '1px solid var(--color-border)',
                      cursor: row.status === 'failed' ? 'pointer' : 'default',
                      backgroundColor:
                        expandedId === row.id ? 'var(--color-surface-elevated)' : undefined,
                    }}
                    onClick={() =>
                      row.status === 'failed' &&
                      setExpandedId(expandedId === row.id ? null : row.id)
                    }
                  >
                    <td
                      style={{
                        padding: 'var(--spacing-2) var(--spacing-3)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {fmtScheduled(row.scheduled_for, row.status)}
                    </td>
                    <td style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}>
                      {row.template_name ?? (
                        <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}>
                      {row.channel ? (
                        <span style={badgeStyle(row.channel === 'sms' ? 'gold' : 'green')}>
                          {row.channel.toUpperCase()}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}>
                      {row.customer_name ?? (
                        <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}>
                      {(row.channel === 'sms' ? row.mobile : row.email) ?? (
                        <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}>
                      <span style={badgeStyle(statusBadgeColor(row.status))}>{row.status}</span>
                      {row.status === 'failed' && (
                        <span
                          style={{
                            marginLeft: 4,
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-muted)',
                          }}
                        >
                          {expandedId === row.id ? '▲' : '▼'}
                        </span>
                      )}
                    </td>
                    <td
                      style={{
                        padding: 'var(--spacing-2) var(--spacing-3)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.sent_at ? (
                        new Date(row.sent_at).toLocaleString('en-AU', {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })
                      ) : (
                        <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}>
                      {row.scheduled_by ?? (
                        <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: 'var(--spacing-2) var(--spacing-3)' }}>
                      {row.status === 'pending' ? (
                        <button
                          style={{
                            ...smallButtonStyle,
                            color: '#dc3545',
                            borderColor: '#dc3545',
                          }}
                          disabled={cancelMutation.isPending}
                          onClick={(e) => {
                            e.stopPropagation();
                            cancelMutation.mutate(row.id);
                          }}
                        >
                          Cancel
                        </button>
                      ) : row.status === 'failed' || row.status === 'cancelled' ? (
                        <button
                          style={{ ...smallButtonStyle, color: 'var(--color-text-muted)' }}
                          title="Remove this entry from the list"
                          disabled={deleteMutation.isPending}
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteMutation.mutate(row.id);
                          }}
                        >
                          Remove
                        </button>
                      ) : (
                        // Sent messages are an immutable record of a real send, so no action.
                        <span style={{ color: 'var(--color-text-muted)' }} />
                      )}
                    </td>
                  </tr>
                );

                if (expandedId === row.id && row.error_message) {
                  return [
                    dataRow,
                    <tr key={`${row.id}-err`}>
                      <td
                        colSpan={9}
                        style={{
                          padding: 'var(--spacing-2) var(--spacing-3)',
                          backgroundColor: 'rgba(220,53,69,0.06)',
                          color: '#dc3545',
                          fontSize: 'var(--font-size-xs)',
                          fontFamily: 'monospace',
                        }}
                      >
                        Error: {row.error_message}
                      </td>
                    </tr>,
                  ];
                }

                return [dataRow];
              })}
            </tbody>
          </table>
        )}
        {total > PAGE_SIZE && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--spacing-2)', marginTop: 'var(--spacing-3)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
            <span>Showing {page * PAGE_SIZE + 1} to {Math.min((page + 1) * PAGE_SIZE, total)} of {total}</span>
            <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'center' }}>
              <button style={{ ...smallButtonStyle, opacity: page === 0 ? 0.5 : 1 }} disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>← Prev</button>
              <span>Page {page + 1} of {pageCount}</span>
              <button style={{ ...smallButtonStyle, opacity: page + 1 >= pageCount ? 0.5 : 1 }} disabled={page + 1 >= pageCount} onClick={() => setPage((p) => p + 1)}>Next →</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── RebookUrlSelect ───────────────────────────────────────────────
// Dropdown for the {rebook_url} variable — defaults to auto-generating
// from the customer's last booking location, or pick a specific link.

function RebookUrlSelect({ value, onChange }: { value: string; onChange: (url: string) => void }) {
  const { data: links = [] } = useQuery({
    queryKey: ['rebook-links'],
    queryFn: () => api.getRebookLinks(),
  });

  return (
    <select style={inputStyle} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">Auto — customer's last booking location</option>
      {(links as any[]).map((link: any) => {
        const slug = link.destination.split('?')[0].replace(/^\//, '');
        const label = slug ? slug.charAt(0).toUpperCase() + slug.slice(1) : 'All locations';
        return (
          <option key={link.code} value={link.url}>
            {label} — {link.url}
          </option>
        );
      })}
    </select>
  );
}

// ─── Contractor Unwell Tab ─────────────────────────────────────────────

function ContractorUnwellTab() {
  const [contractorId, setContractorId] = useState<number | null>(null);
  const [date, setDate] = useState(() =>
    // Brisbane local date (en-CA gives YYYY-MM-DD). toISOString() returns UTC,
    // which rolls back one day before 10am Brisbane — wrong default.
    new Date().toLocaleDateString('en-CA', { timeZone: 'Australia/Brisbane' }),
  );
  const [loaded, setLoaded] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState<{
    sent: number;
    failed: number;
    skipped: number;
    total: number;
  } | null>(null);

  const { data: contractors = [] } = useQuery<any[]>({
    queryKey: ['contractors'],
    queryFn: () => api.getContractors(),
  });

  const {
    data: preview,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['contractor-unwell-preview', contractorId, date],
    queryFn: () => api.getContractorUnwellPreview(contractorId!, date),
    enabled: false,
  });

  const sendMutation = useMutation({
    mutationFn: () => api.sendContractorUnwellBatch(contractorId!, date),
    onSuccess: (data) => {
      setResult(data);
      setConfirmOpen(false);
    },
  });

  function handleLoadPreview() {
    if (!contractorId || !date) return;
    setResult(null);
    setLoaded(true);
    refetch();
  }

  const items = preview?.items || [];
  const sendableItems = items.filter((i) => i.canSend);
  const cannotContactItems = items.filter((i) => !i.canSend);
  const canSend = sendableItems.length > 0 && !sendMutation.isPending;

  return (
    <div>
      <div style={pageCardStyle}>
        <h3
          style={{ margin: 0, marginBottom: 'var(--spacing-2)', fontSize: 'var(--font-size-md)' }}
        >
          🤒 Notify customers of sick contractor
        </h3>
        <p
          style={{
            margin: '0 0 var(--spacing-3) 0',
            color: 'var(--color-text-muted)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          Select a contractor and a date. All BOOKED customers for that day will be sent the "Contractor
          Unwell" SMS with a link to reschedule.
        </p>

        <div
          style={{
            display: 'flex',
            gap: 'var(--spacing-3)',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
          }}
        >
          <div style={{ flex: '1 1 250px' }}>
            <label
              style={{
                display: 'block',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'var(--color-text-muted)',
                marginBottom: '4px',
              }}
            >
              Contractor
            </label>
            <select
              style={inputStyle}
              value={contractorId ?? ''}
              onChange={(e) => {
                setContractorId(e.target.value ? Number(e.target.value) : null);
                setLoaded(false);
                setResult(null);
              }}
            >
              <option value="">— Select a contractor —</option>
              {contractors.map((b: any) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: '0 1 180px' }}>
            <label
              style={{
                display: 'block',
                fontSize: 'var(--font-size-xs)',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                color: 'var(--color-text-muted)',
                marginBottom: '4px',
              }}
            >
              Date
            </label>
            <input
              type="date"
              style={inputStyle}
              value={date}
              onChange={(e) => {
                setDate(e.target.value);
                setLoaded(false);
                setResult(null);
              }}
            />
          </div>
          <button
            style={{ ...primaryButtonStyle, minWidth: 160 }}
            onClick={handleLoadPreview}
            disabled={!contractorId || !date || isFetching}
          >
            {isFetching ? 'Loading…' : 'Load preview'}
          </button>
        </div>
      </div>

      {loaded && preview && (
        <div
          style={{
            ...pageCardStyle,
            marginTop: 'var(--spacing-3)',
            borderLeft: '3px solid var(--color-accent)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 'var(--spacing-2)',
            }}
          >
            <div>
              <h3 style={{ margin: 0, fontSize: 'var(--font-size-md)' }}>
                {preview.contractorName} —{' '}
                {new Date(preview.date).toLocaleDateString('en-AU', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  timeZone: 'Australia/Brisbane',
                })}
              </h3>
              <p
                style={{
                  margin: '4px 0 0 0',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-text-muted)',
                }}
              >
                {items.length} customer{items.length === 1 ? '' : 's'} booked ·{' '}
                <strong>{sendableItems.length}</strong> will receive SMS
                {cannotContactItems.length > 0 && (
                  <>
                    {' · '}
                    <span style={{ color: '#dc3545', fontWeight: 600 }}>
                      {cannotContactItems.length} cannot be contacted
                    </span>
                  </>
                )}
                {' · template: '}
                {preview.templateName}
              </p>
            </div>
            <button
              style={{ ...primaryButtonStyle, opacity: canSend ? 1 : 0.5 }}
              onClick={() => setConfirmOpen(true)}
              disabled={!canSend}
            >
              Send to {sendableItems.length} customer{sendableItems.length === 1 ? '' : 's'}
            </button>
          </div>

          {cannotContactItems.length > 0 && (
            <div
              style={{
                background: 'rgba(220, 53, 69, 0.08)',
                border: '1px solid rgba(220, 53, 69, 0.3)',
                borderRadius: '4px',
                padding: 'var(--spacing-2) var(--spacing-3)',
                marginBottom: 'var(--spacing-2)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              <strong>
                ⚠️ {cannotContactItems.length} customer
                {cannotContactItems.length === 1 ? '' : 's'} cannot be reached by SMS
              </strong>{' '}
              — call them manually or contact in person.
            </div>
          )}

          {items.length === 0 ? (
            <p
              style={{
                margin: 0,
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-muted)',
                fontStyle: 'italic',
              }}
            >
              No BOOKED customers for this contractor on this date.
            </p>
          ) : (
            <div
              style={{
                maxHeight: '500px',
                overflowY: 'auto',
                border: '1px solid var(--color-border)',
                borderRadius: '4px',
                marginTop: 'var(--spacing-2)',
              }}
            >
              <table
                style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: 'var(--font-size-sm)',
                }}
              >
                <thead
                  style={{
                    position: 'sticky',
                    top: 0,
                    background: 'var(--color-surface)',
                    zIndex: 1,
                  }}
                >
                  <tr>
                    <th style={headerCellStyle}>Status</th>
                    <th style={headerCellStyle}>Customer</th>
                    <th style={headerCellStyle}>Mobile</th>
                    <th style={headerCellStyle}>Appointment</th>
                    <th style={headerCellStyle}>Message Preview</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr
                      key={item.bookingId}
                      style={{
                        borderTop: '1px solid var(--color-border)',
                        background: item.canSend ? undefined : 'rgba(220, 53, 69, 0.04)',
                      }}
                    >
                      <td style={bodyCellStyle}>
                        {item.canSend ? (
                          <span style={badgeStyle('green')}>Will send</span>
                        ) : (
                          <span style={badgeStyle('red')} title={item.skipReason || ''}>
                            ⚠ Skip
                          </span>
                        )}
                      </td>
                      <td style={bodyCellStyle}>{item.customerName || '—'}</td>
                      <td style={{ ...bodyCellStyle, fontFamily: 'monospace' }}>
                        {item.mobile || <span style={{ color: '#dc3545' }}>— (no phone)</span>}
                      </td>
                      <td style={bodyCellStyle}>{item.appointmentDate}</td>
                      <td
                        style={{
                          ...bodyCellStyle,
                          fontFamily: 'monospace',
                          fontSize: '11px',
                          maxWidth: '400px',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {item.canSend ? (
                          item.renderedBody
                        ) : (
                          <span style={{ color: '#dc3545', fontStyle: 'italic' }}>
                            {item.skipReason}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {result && (
        <div
          style={{
            ...pageCardStyle,
            marginTop: 'var(--spacing-3)',
            borderLeft: '3px solid #28a745',
          }}
        >
          <h3
            style={{ margin: 0, marginBottom: 'var(--spacing-1)', fontSize: 'var(--font-size-md)' }}
          >
            ✓ Batch complete
          </h3>
          <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
            <strong>{result.sent}</strong> sent · <strong>{result.failed}</strong> failed ·{' '}
            <strong>{result.skipped}</strong> skipped · {result.total} total
          </p>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmOpen}
        title="Send Contractor Unwell SMS?"
        message={`This will send the SMS to ${sendableItems.length} customer${sendableItems.length === 1 ? '' : 's'}.${cannotContactItems.length > 0 ? ` ${cannotContactItems.length} will be skipped (no phone or opted out).` : ''} This cannot be undone.`}
        confirmLabel={`Send to ${sendableItems.length}`}
        tone="warning"
        onConfirm={() => sendMutation.mutate()}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
